/** tm/pages/SettingsPage.tsx — 설정 (탭: 일반 / 업무 템플릿 / 카테고리 / AI 프리셋 / 과금) */
import React, { useState, useEffect, useRef } from 'react';
import type { TMStore } from '../hooks';
import { C, T, R, Btn, Field, Badge, Empty, SectionCard } from '../components/ui';
import * as api from '../api';
import type { TaskCategory, TaskTemplate } from '../types';
import { ownerOf } from '../stepOwner';
import type { Owner } from '../stepOwner';
import { OwnerDetail, OwnerSelect } from '../components/StepOwnerFields';
import JudgeSelect from '../components/JudgeSelect';   // 결과 단계 말(2026-10-28)
import type { OwnerCfg } from '../components/StepOwnerFields';
import StepSettingModal from '../components/StepSettingModal';
import AIStepConfigModal from '../components/AIStepConfigModal';
import AIBizplanModal from '../components/AIBizplanModal';
import LoanFundSettings from '../components/LoanFundSettings';
import { DndContext, closestCenter, DragOverlay, useSensor, useSensors, PointerSensor, DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function useConfirmModal() {
  const [state, setState] = useState<{msg:string; onOk:()=>void}|null>(null);
  const ask = (msg: string, onOk: () => void) => setState({ msg, onOk });
  const el = state ? (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={() => setState(null)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
      <div style={{ position: 'relative', padding: 24, background: '#fff', borderRadius: 16, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', width: 360, textAlign: 'center' }}>
        <div style={{ fontSize: T.md, fontWeight: 700, marginBottom: 16, lineHeight: 1.5 }}>{state.msg}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setState(null)} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, fontSize: T.md, fontWeight: 600, cursor: 'pointer' }}>취소</button>
          <button onClick={() => { state.onOk(); setState(null); }} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: '#DC2626', color: '#fff', fontSize: T.md, fontWeight: 700, cursor: 'pointer' }}>확인</button>
        </div>
      </div>
    </div>
  ) : null;
  return { ask, el };
}

type Tab = 'templates' | 'collect' | 'categories' | 'loan_fund' | 'ai' | 'company' | 'contact' | 'fee_stats' | 'billing' | 'db_tools' | 'tag_manage';

const TABS: { id: Tab; icon: string; label: string }[] = [
  { id: 'templates', icon: '📋', label: '업무 템플릿' },
  { id: 'collect', icon: '📎', label: '자료수집 템플릿' },
  { id: 'categories', icon: '📂', label: '카테고리' },
  { id: 'loan_fund', icon: '🏦', label: '대출 자금 종류' },   // 2026-10-27 신청할 곳 밑 「어떤 자금」 트리
  { id: 'ai', icon: '🤖', label: 'AI 프리셋' },
  { id: 'company', icon: '🏢', label: '소속 관리' },
  { id: 'contact', icon: '💬', label: '고객 문의 연락처' },
  { id: 'fee_stats', icon: '💰', label: '상담료 통계' },
  { id: 'billing', icon: '💳', label: '과금 현황' },
  { id: 'db_tools', icon: '🛠️', label: 'DB 관리' },
  { id: 'tag_manage' as Tab, icon: '🏷️', label: '태그 관리' },
];

interface Props { store: TMStore; navParams?: { tab?: string }; onNavigate?: (id: string, params?: unknown) => void }


function TagManageTab() {
  const [presets, setPresets] = useState<api.TagPreset[]>([]);
  const [initLoading, setInitLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editCategory, setEditCategory] = useState('');

  async function load(init = false) {
    if (init) setInitLoading(true);
    try { const r = await api.getTagPresets(); setPresets(r.data || []); } catch {}
    if (init) setInitLoading(false);
  }
  useEffect(() => { load(true); }, []);

  const categories = Array.from(new Set(presets.map(p => p.category || 'custom')));

  async function handleAdd() {
    if (!newLabel.trim()) return;
    try {
      await api.createTagPreset({ label: newLabel.trim(), category: newCategory.trim() || 'custom' });
      setNewLabel(''); setNewCategory('');
      await load();
    } catch {}
  }

  async function handleDelete(id: string) {
    if (!confirm('삭제하시겠습니까?')) return;
    try { await api.deleteTagPreset(id); await load(); } catch {}
  }

  async function handleUpdate(id: string) {
    try {
      await api.updateTagPreset(id, { label: editLabel, category: editCategory || 'custom' });
      setEditId(null);
      await load();
    } catch {}
  }

  if (initLoading) return <div style={{ padding: 20, textAlign: 'center', color: C.muted }}>로딩 중...</div>;

  return (
    <div>
      <TabTitle icon="🏷️" title="태그 관리" desc="업체별 태그 프리셋 관리 및 카테고리 편집" />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="태그명"
          onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
          style={{ flex: 1, padding: '8px 12px', borderRadius: R.sm, border: '1px solid ' + C.border, fontSize: T.sm, outline: 'none' }} />
        <div style={{ position: 'relative', width: 140 }}>
          <input value={newCategory} onChange={e => { setNewCategory(e.target.value); setCatOpen(true); }}
            onFocus={() => setCatOpen(true)} onBlur={() => setTimeout(() => setCatOpen(false), 150)}
            placeholder="카테고리"
            style={{ width: '100%', padding: '8px 12px', borderRadius: R.sm, border: '1px solid ' + C.border, fontSize: T.sm, outline: 'none', boxSizing: 'border-box' as const }} />
          {catOpen && categories.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#fff', border: '1px solid ' + C.border, borderRadius: R.sm, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 150, overflowY: 'auto' }}>
              {categories.filter(c => !newCategory || c.includes(newCategory)).map(c => (
                <div key={c} onMouseDown={() => { setNewCategory(c); setCatOpen(false); }}
                  style={{ padding: '6px 12px', cursor: 'pointer', fontSize: T.xs, borderBottom: '1px solid ' + C.bg }}>{c}</div>
              ))}
            </div>
          )}
        </div>
        <button onClick={handleAdd}
          style={{ padding: '8px 16px', borderRadius: R.sm, border: 'none', background: C.accent, color: C.white, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const }}>추가</button>
      </div>
      {categories.map(cat => (
        <div key={cat} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: T.xs, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: 'uppercase' as const }}>{cat} ({presets.filter(p => (p.category || 'custom') === cat).length})</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {presets.filter(p => (p.category || 'custom') === cat).map(p => (
              editId === p.id ? (
                <div key={p.id} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', padding: '3px 6px', borderRadius: 20, border: '1px solid ' + C.accent, background: C.accentLt }}>
                  <input value={editLabel} onChange={e => setEditLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleUpdate(p.id); if (e.key === 'Escape') setEditId(null); }}
                    autoFocus style={{ padding: '2px 6px', borderRadius: 4, border: '1px solid ' + C.accent, fontSize: T.xs, width: 80, outline: 'none' }} />
                  <select value={editCategory} onChange={e => setEditCategory(e.target.value)}
                    style={{ padding: '2px 4px', borderRadius: 4, border: '1px solid ' + C.border, fontSize: T.xs, outline: 'none' }}>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button onClick={() => handleUpdate(p.id)} style={{ fontSize: T.xs, padding: '2px 6px', borderRadius: 4, border: 'none', background: C.green, color: '#fff', cursor: 'pointer' }}>✓</button>
                  <button onClick={() => setEditId(null)} style={{ fontSize: T.xs, padding: '2px 6px', borderRadius: 4, border: '1px solid ' + C.border, background: '#fff', cursor: 'pointer' }}>✕</button>
                </div>
              ) : (
                <span key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 20, background: C.bg, border: '1px solid ' + C.border, fontSize: T.xs, transition: 'all 0.15s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = C.accent; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = C.border; }}>
                  <span style={{ cursor: 'pointer' }} onClick={() => { setEditId(p.id); setEditLabel(p.label); setEditCategory(p.category || ''); }}>{p.label}</span>
                  <button onClick={() => handleDelete(p.id)} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: T.xs, padding: 0, marginLeft: 2 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = C.red; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = C.muted; }}>✕</button>
                </span>
              )
            ))}
          </div>
        </div>
      ))}
      {presets.length === 0 && <div style={{ textAlign: 'center', padding: 30, color: C.muted }}>등록된 태그가 없습니다</div>}
    </div>
  );
}

export default function SettingsPage({ store, navParams, onNavigate }: Props) {
  const [tab, setTab] = useState<Tab>('templates');

  const initialTabSet = useRef(false);
  useEffect(() => {
    if (!initialTabSet.current && navParams?.tab && TABS.some(t => t.id === navParams.tab)) {
      setTab(navParams.tab as Tab);
      initialTabSet.current = true;
    }
  }, [navParams?.tab]);

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* 탭 사이드 */}
      <div style={{ width: 160, borderRight: `1px solid ${C.border}`, background: C.bg, padding: '8px 0' }}>
        {TABS.map(t => (
          <div key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '9px 14px', cursor: 'pointer', fontSize: T.sm,
            background: tab === t.id ? C.white : 'transparent',
            color: tab === t.id ? C.accent : C.sub, fontWeight: tab === t.id ? 700 : 400,
          }}>{t.icon} {t.label}</div>
        ))}
      </div>
      {/* 콘텐츠 */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {tab === 'templates' && <TemplatesTab store={store} onNavigateToAI={() => setTab('ai')} />}
        {tab === 'collect' && <CollectTemplatesTab />}
        {tab === 'categories' && <CategoriesTab store={store} />}
        {tab === 'loan_fund' && <LoanFundSettings />}
        {tab === 'ai' && <AIPresetsTab store={store} />}
        {tab === 'company' && <CompanyMgmtTab />}
        {tab === 'contact' && <ClientContactTab />}
        {tab === 'fee_stats' && <FeeStatsTab store={store} />}
        {tab === 'billing' && <BillingTab />}
        {tab === 'db_tools' && <DbToolsTab store={store} onNavigate={onNavigate} />}
        {tab === 'tag_manage' && <TagManageTab />}
      </div>
    </div>
  );
}

function IMEInput({ value, onChange, ...props }: { value: string; onChange: (v: string) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [local, setLocal] = React.useState(value);
  const composing = React.useRef(false);
  React.useEffect(() => { if (!composing.current) setLocal(value); }, [value]);
  return <input value={local}
    onCompositionStart={() => { composing.current = true; }}
    onCompositionEnd={(e: React.CompositionEvent<HTMLInputElement>) => { composing.current = false; onChange(e.currentTarget.value); }}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setLocal(e.target.value); if (!composing.current) onChange(e.target.value); }}
    {...props} />;
}

function TabTitle({ icon, title, desc }: { icon: string; title: string; desc?: string }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: T.xl }}>{icon}</span>
        <span style={{ fontSize: T.xl, fontWeight: 700, color: C.text }}>{title}</span>
      </div>
      {desc && <div style={{ fontSize: T.xs, color: C.muted }}>{desc}</div>}
    </div>
  );
}

// ── 업무 템플릿 ──
// owner · ask · ask_items = 누가 할 일 · 안내 글 · 받을 서류(2026-10-28 · tm/stepOwner · 서버 services/stepOwner)
interface TplStep { name: string; type: string; due_days: number; settings?: Record<string, unknown>; owner?: Owner; ask?: string; ask_items?: string[]; judge?: { yes: string; no: string } | null }
/** 저장할 단계 모양 — 담당이 임지도사면 안내 칸은 싣지 않는다 */
const tplStepOut = (s: TplStep) => ({ name: s.name, type: s.type, due_days: s.due_days, note: '', collect_ids: [], use_ai: false, settings: s.settings || {}, judge: s.judge && String(s.judge.yes || '').trim() ? { yes: s.judge.yes.trim(), no: (s.judge.no || '').trim() } : null, ...(ownerOf(s.owner) !== 'jido' ? { owner: ownerOf(s.owner), ask: (s.ask || '').trim(), ask_items: s.ask_items || [] } : {}) });
/** 벤처 화면과 이어진 단계(이름 · 종류 · 순서는 벤처 화면 기준 — 담당 · 안내만 고침) */
const ventureLocked = (s: TplStep) => !!(s.settings && typeof s.settings.venture_step === 'number');
const STEP_TYPES: [string,string][] = [['work','일반업무'],['collect','자료수집'],['ai_questionnaire','AI 질문지'],['ai_bizplan','AI 사업계획서'],['confirm','검토'],['report','제출']];
const DUE_OPTIONS = [0,3,7,14,30];

function TemplatesTab({ store, onNavigateToAI }: { store: TMStore; onNavigateToAI?: () => void }) {
  const confirm = useConfirmModal();
  const { taskTemplates } = store;
  const [showAdd, setShowAdd] = useState(false);
  const [formName, setFormName] = useState('');
  const [formCatPath, setFormCatPath] = useState<string[]>([]);
  const [formSteps, setFormSteps] = useState<TplStep[]>([{ name: '', type: 'work', due_days: 7 }]);
  const [expandId, setExpandId] = useState<string|null>(null);
  const [editSteps, setEditSteps] = useState<TplStep[]>([]);
  const [editName, setEditName] = useState('');
  const [editCatPath, setEditCatPath] = useState<string[]>([]);
  const [toast, setToast] = useState('');
  const [dragIdx, setDragIdx] = useState<number|null>(null);

  // 카테고리 경로 선택 헬퍼
  function getCatChildren(parentId: string | null): TaskCategory[] {
    return store.taskCategories.filter(c => c.parent_id === parentId).sort((a,b) => a.sort_order - b.sort_order);
  }
  function renderCatPathSelector(catPath: string[], setCatPath: (p: string[]) => void) {
    const levels: { parentId: string | null; selected: string }[] = [];
    let pid: string | null = null;
    for (const name of catPath) {
      const cat = store.taskCategories.find(c => c.parent_id === pid && c.name === name);
      levels.push({ parentId: pid, selected: name });
      pid = cat?.id || null;
    }
    // 다음 레벨 (아직 선택 안 됨)
    const nextChildren = getCatChildren(pid);
    if (nextChildren.length > 0) levels.push({ parentId: pid, selected: '' });

    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 4 }}>업무 유형</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {levels.map((lv, li) => {
            const children = getCatChildren(lv.parentId);
            if (children.length === 0) return null;
            return (
              <select key={li} value={lv.selected} onChange={e => {
                const newPath = catPath.slice(0, li);
                if (e.target.value) newPath.push(e.target.value);
                setCatPath(newPath);
              }} style={{ padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 11, outline: 'none', color: lv.selected ? '#1F2937' : '#9CA3AF', background: '#fff', fontWeight: lv.selected ? 600 : 400, minWidth: 90 }}>
                <option value="">선택</option>
                {children.map(ch => <option key={ch.id} value={ch.name}>{ch.name}</option>)}
              </select>
            );
          })}
        </div>
      </div>
    );
  }
  const [stepSettingModal, setStepSettingModal] = useState<{stepIdx: number; stepName: string; stepType: string; isEdit: boolean} | null>(null);
  const [aiStepConfigModal, setAiStepConfigModal] = useState<{stepIdx: number; stepName: string; isEdit: boolean; stepType?: string} | null>(null);
  const [aiBizplanModal, setAiBizplanModal] = useState<{stepIdx: number; stepName: string; isEdit: boolean} | null>(null);
  const [collectTemplatesList, setCollectTemplatesList] = useState<{id:string;name:string;itemCount?:number}[]>([]);

  useEffect(() => {
    api.getCollectTemplates()
      .then(list => setCollectTemplatesList(list))
      .catch(() => {});
  }, []);

  function showToastMsg(msg: string) { setToast(msg); setTimeout(() => setToast(''), 3000); }

  function addFormStep() { setFormSteps(p => [...p, { name: '', type: 'work', due_days: 7 }]); }
  function updFormStep(idx: number, key: string, val: string | number) { setFormSteps(p => p.map((s,i) => i===idx ? {...s, [key]: val} : s)); }
  /** 담당 · 안내 글 · 받을 서류 고치기(만들기 · 고치기 공용) */
  function patchOwner(isEdit: boolean, idx: number, patch: OwnerCfg) { (isEdit ? setEditSteps : setFormSteps)(p => p.map((s, i) => (i === idx ? { ...s, ...patch } : s))); }
  function removeFormStep(idx: number) { setFormSteps(p => p.filter((_,i) => i!==idx)); }

  async function handleAdd() {
    if (!formName.trim()) return;
    try {
      const steps = formSteps.filter(s => s.name.trim()).map(tplStepOut);
      await api.createTaskTemplate({ name: formName, icon: '📋', total_days: Math.max(...steps.map(s => s.due_days), 0), steps, cat_path: formCatPath });
      await store.refresh();
      setShowAdd(false); setFormName(''); setFormCatPath([]); setFormSteps([{ name: '', type: 'work', due_days: 7 }]);
      showToastMsg('업무 템플릿 등록됨');
    } catch (e: unknown) { showToastMsg(e instanceof Error ? e.message : '실패'); }
  }

  function toggleExpand(t: typeof taskTemplates[0]) {
    if (expandId === t.id) { setExpandId(null); return; }
    setExpandId(t.id);
    setEditName(t.name);
    setEditCatPath(t.cat_path || []);
    setEditSteps((t.steps || []).map(s => ({ name: s.name, type: s.type || 'work', due_days: s.due_days || 7, settings: (s as unknown as Record<string, unknown>).settings as Record<string, unknown> || undefined, owner: ownerOf(s.owner), ask: s.ask || '', ask_items: Array.isArray(s.ask_items) ? s.ask_items : [], judge: s.judge || null })));
  }

  function addEditStep() { setEditSteps(p => [...p, { name: '', type: 'work', due_days: 7 }]); }
  function updEditStep(idx: number, key: string, val: string | number) { setEditSteps(p => p.map((s,i) => i===idx ? {...s, [key]: val} : s)); }
  function removeEditStep(idx: number) { setEditSteps(p => p.filter((_,i) => i!==idx)); }

  async function saveEdit() {
    if (!expandId || !editName.trim()) return;
    try {
      const steps = editSteps.filter(s => s.name.trim()).map(tplStepOut);
      const r = await api.updateTaskTemplate(expandId, { name: editName, total_days: Math.max(...steps.map(s => s.due_days), 0), steps, cat_path: editCatPath } as Partial<TaskTemplate>);
      await store.refresh();
      // 진행 중 업무의 아직 시작 안 한 단계에도 반영됐으면 몇 개인지(2026-10-28 지도사 확정)
      const applied = Number((r && (r.data as unknown as { applied?: number }))?.applied || 0);
      showToastMsg(applied ? `저장됨 · 진행 중 업무의 앞으로 올 단계 ${applied}개에도 반영했어요` : '저장됨');
    } catch (e: unknown) { showToastMsg(e instanceof Error ? e.message : '실패'); }
  }

  function handleDrop(fromIdx: number, toIdx: number, isEdit: boolean) {
    const setter = isEdit ? setEditSteps : setFormSteps;
    setter(p => {
      const arr = [...p];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
    setDragIdx(null);
  }

  function renderStepRow({ step, idx, isEdit, steps }: { step: TplStep; idx: number; isEdit: boolean; steps: TplStep[] }) {
    const updFn = isEdit ? updEditStep : updFormStep;
    const rmFn = isEdit ? removeEditStep : removeFormStep;
    const settingBtnLabel = step.type === 'collect' ? '📂 자료수집 설정' : step.type === 'ai_questionnaire' ? '🤖 AI 질문지 설정' : step.type === 'ai_bizplan' ? '🤖 AI 사업계획서 설정' : null;
    function handleSettingClick() {
      if (step.type === 'collect') setStepSettingModal({ stepIdx: idx, stepName: step.name, stepType: step.type, isEdit });
      else if (step.type === 'ai_questionnaire') setAiStepConfigModal({ stepIdx: idx, stepName: step.name, isEdit, stepType: 'ai_questionnaire' });
      else if (step.type === 'ai_bizplan') setAiStepConfigModal({ stepIdx: idx, stepName: step.name, isEdit, stepType: 'ai_bizplan' });
    }
    const isCustomDue = !DUE_OPTIONS.includes(step.due_days);
    const typeColor = step.type === 'collect' ? '#2563EB' : step.type === 'ai_questionnaire' || step.type === 'ai_bizplan' ? '#7C3AED' : '#6B7280';
    const locked = ventureLocked(step);   // 벤처 화면과 이어진 단계 — 담당 · 안내만
    const typeBg = step.type === 'collect' ? '#EFF6FF' : step.type === 'ai_questionnaire' || step.type === 'ai_bizplan' ? '#F5F3FF' : '#F3F4F6';
    return (
      <div style={{marginBottom:6}}>
        <div draggable={!locked} onDragStart={() => setDragIdx(idx)}
          onDragOver={e => e.preventDefault()}
          onDrop={() => { if (dragIdx !== null && dragIdx !== idx) handleDrop(dragIdx, idx, isEdit); }}
          style={{display:'flex', gap:6, alignItems:'center', opacity: dragIdx===idx ? 0.4 : 1, padding:'8px 10px', borderRadius:8, background:'#fff', border:`1px solid ${C.border}`}}>
          <span style={{cursor: locked ? 'default' : 'grab', color:'#CBD5E1', fontSize:14, flexShrink:0, width:16, textAlign:'center', lineHeight:1, visibility: locked ? 'hidden' : 'visible'}}>⠿</span>
          <span style={{width:22, height:22, borderRadius:'50%', background:C.accent, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#fff', flexShrink:0}}>{idx+1}</span>
          <IMEInput value={step.name} readOnly={locked} title={locked ? '벤처인증 화면 단계 이름이에요 — 담당 · 안내만 고칠 수 있어요' : undefined} onChange={v => { if (!locked) updFn(idx, 'name', v); }} placeholder="하위업무명"
            style={{flex:1, padding:'7px 10px', borderRadius:6, border:`1px solid ${C.border}`, fontSize:13, outline:'none', color:'#1F2937', fontWeight:step.name?500:400, minWidth:0}} />
          <OwnerSelect value={step.owner} onChange={v => patchOwner(isEdit, idx, { owner: v })} />
          <JudgeSelect value={step.judge} onChange={j => patchOwner(isEdit, idx, { judge: j })} />
          <select value={step.type} disabled={locked} onChange={e => updFn(idx, 'type', e.target.value)}
            style={{width:80, flexShrink:0, padding:'7px 8px', borderRadius:6, border:`1px solid ${typeColor}33`, fontSize:11, outline:'none', color:typeColor, background:typeBg, fontWeight:600, cursor:'pointer', boxSizing:'border-box' as const, textAlign:'center'}}>
            {STEP_TYPES.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {isCustomDue ? (
            <div style={{width:72, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', gap:1, boxSizing:'border-box' as const}}>
              <span style={{fontSize:11, color:'#9CA3AF', fontWeight:600}}>D+</span>
              <input type="number" value={step.due_days === -1 ? '' : step.due_days} onChange={e => { const v = parseInt(e.target.value); updFn(idx, 'due_days', isNaN(v) ? -1 : v); }}
                placeholder="0" style={{width:34, padding:'5px 2px', borderRadius:5, border:`1px solid ${C.border}`, fontSize:12, textAlign:'center', color:'#1F2937', outline:'none', fontWeight:600}} />
            </div>
          ) : (
            <select value={step.due_days} onChange={e => updFn(idx, 'due_days', parseInt(e.target.value))}
              style={{width:72, flexShrink:0, padding:'7px 6px', borderRadius:6, border:`1px solid ${C.border}`, fontSize:11, outline:'none', color:'#1F2937', background:'#fff', fontWeight:600, cursor:'pointer', boxSizing:'border-box' as const, textAlign:'center'}}>
              {DUE_OPTIONS.map(d => <option key={d} value={d}>D+{d}</option>)}
              <option value={-1}>직접입력</option>
            </select>
          )}
          <span onClick={() => { if (!locked) rmFn(idx); }} style={{width:20, flexShrink:0, color:'#DC2626', cursor:'pointer', fontSize:13, opacity:0.5, textAlign:'center', visibility: locked ? 'hidden' : 'visible'}} title="삭제">✕</span>
        </div>
        <OwnerDetail cfg={step} onChange={p => patchOwner(isEdit, idx, p)} />
        {settingBtnLabel && (
          <button onClick={handleSettingClick} style={{marginLeft:32, marginTop:3, padding:'4px 10px', borderRadius:6, border:`1px solid ${typeColor}33`, background:typeBg, color:typeColor, fontSize:11, fontWeight:600, cursor:'pointer'}}>
            {settingBtnLabel} ▼
          </button>
        )}
      </div>
    );
  }

  return (<>
    {confirm.el}
    <div style={{maxWidth:700}}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <TabTitle icon="📋" title="업무 템플릿" desc="동행상담 후 등록되는 업무의 단계 구조를 설정합니다." />
        <Btn onClick={() => setShowAdd(!showAdd)}>{showAdd ? '취소' : '+ 추가'}</Btn>
      </div>

      {/* 추가 폼 */}
      {showAdd && (
        <div style={{ padding: 16, borderRadius: 12, background: C.white, border: `1px solid ${C.border}`, marginBottom: 12 }}>
          <div style={{marginBottom:10}}>
            <label style={{fontSize:T.sm, fontWeight:600, color:C.sub, display:'block', marginBottom:4}}>업무명 *</label>
            <input value={formName} onChange={e => setFormName(e.target.value)} placeholder="예: 벤처인증"
              style={{width:'100%', padding:'10px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box'}} />
          </div>
          {renderCatPathSelector(formCatPath, setFormCatPath)}
          <div style={{fontSize:T.sm, fontWeight:600, color:C.sub, marginBottom:6}}>하위업무</div>
          {formSteps.map((s,i) => renderStepRow({ step: s, idx: i, isEdit: false, steps: formSteps }))}
          <button onClick={addFormStep} style={{width:'100%', padding:8, borderRadius:8, border:`2px dashed ${C.border}`, background:'transparent', fontSize:T.sm, color:C.muted, cursor:'pointer', marginBottom:10}}>+ 하위업무 추가</button>
          <Btn onClick={handleAdd} disabled={!formName.trim()} style={{ width: '100%' }}>등록</Btn>
        </div>
      )}

      {taskTemplates.length === 0 && !showAdd && <Empty icon="📋" message="등록된 템플릿이 없습니다" />}
      {taskTemplates.map(t => {
        const isOpen = expandId === t.id;
        return (
          <div key={t.id} style={{ borderRadius: 10, background: C.white, border: `1px solid ${isOpen?C.accent:C.border}`, marginBottom: 6, overflow:'hidden' }}>
            <div onClick={() => toggleExpand(t)} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor:'pointer' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: T.sm, fontWeight: 600 }}>{t.name}{t.system_key ? <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: '#EEF2F6', color: '#3B5268', fontSize: 10, fontWeight: 700, verticalAlign: 'middle' }}>벤처인증 화면 연동</span> : null}</div>
                <div style={{ fontSize: T.xs, color: C.muted }}>{(t.steps || []).length}단계</div>
              </div>
              <Badge>{t.is_active ? '활성' : '비활성'}</Badge>
              {!t.system_key && <button onClick={(e)=>{e.stopPropagation();confirm.ask('삭제하시겠습니까?',async()=>{try{await api.deleteTaskTemplate(t.id);await store.refresh();}catch{}});}} style={{fontSize:T.xs,padding:'3px 8px',borderRadius:R.sm,border:'1px solid #DC2626',background:'white',color:'#DC2626',cursor:'pointer'}}>삭제</button>}
              <span style={{color:C.muted, fontSize:T.md}}>{isOpen ? '▲' : '▼'}</span>
            </div>
            {isOpen && (
              <div style={{padding:'0 14px 14px', borderTop:`1px solid ${C.border}`}}>
                <div style={{marginTop:10, marginBottom:8}}>
                  <label style={{fontSize:T.sm, fontWeight:600, color:C.sub, display:'block', marginBottom:4}}>업무명</label>
                  <input value={editName} onChange={e => setEditName(e.target.value)}
                    style={{width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:T.md, outline:'none', boxSizing:'border-box'}} />
                </div>
                {renderCatPathSelector(editCatPath, setEditCatPath)}
                <div style={{fontSize:T.sm, fontWeight:600, color:C.sub, marginBottom:6}}>하위업무</div>
                {editSteps.map((s,i) => renderStepRow({ step: s, idx: i, isEdit: true, steps: editSteps }))}
                {t.system_key && <div style={{fontSize:T.xs, color:C.muted, margin:'2px 0 8px', lineHeight:1.5}}>단계 이름 · 순서는 벤처인증 화면과 같아요. 단계마다 담당 · 안내 글 · 받을 서류만 고쳐 주세요 — 저장하면 진행 중인 벤처 업무의 앞으로 올 단계에도 반영돼요.</div>}
                {!t.system_key && <button onClick={addEditStep} style={{width:'100%', padding:6, borderRadius:8, border:`2px dashed ${C.border}`, background:'transparent', fontSize:T.xs, color:C.muted, cursor:'pointer', marginBottom:8}}>+ 하위업무 추가</button>}
                <Btn onClick={saveEdit} style={{width:'100%'}}>저장</Btn>
              </div>
            )}
          </div>
        );
      })}
      {toast && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', padding: '12px 24px', borderRadius: R.md, background: C.text, color: C.white, fontSize: T.sm, zIndex: 9999 }}>{toast}</div>}
      {stepSettingModal && (() => {
        const steps = stepSettingModal.isEdit ? editSteps : formSteps;
        const curSettings = steps[stepSettingModal.stepIdx]?.settings || {};
        return (
          <StepSettingModal
            stepIndex={stepSettingModal.stepIdx}
            stepName={stepSettingModal.stepName}
            stepType={stepSettingModal.stepType}
            settings={curSettings}
            collectTemplates={collectTemplatesList}
            onSave={(s) => {
              const setter = stepSettingModal.isEdit ? setEditSteps : setFormSteps;
              setter(p => p.map((st, i) => i === stepSettingModal.stepIdx ? { ...st, settings: s } : st));
              setStepSettingModal(null);
              showToastMsg('단계 설정 저장됨');
            }}
            onClose={() => setStepSettingModal(null)}
          />
        );
      })()}
      {aiStepConfigModal && (() => {
        const steps = aiStepConfigModal.isEdit ? editSteps : formSteps;
        const curSettings = steps[aiStepConfigModal.stepIdx]?.settings || {};
        return (
          <AIStepConfigModal
            aiType={(aiStepConfigModal.stepType || 'ai_questionnaire') as 'ai_questionnaire' | 'ai_bizplan'}
            presets={store.aiPresets || []}
            initialPresetId={(curSettings.presetId as string) || ''}
            initialPrompt={(curSettings.prompt as string) || ''}
            initialFiles={curSettings.files as { label: string; name: string; s3_key: string }[] | undefined}
            initialIncludeCollect={curSettings.includeCollect as boolean | undefined}
            onSave={(cfg) => {
              const setter = aiStepConfigModal.isEdit ? setEditSteps : setFormSteps;
              setter(p => p.map((st, i) => i === aiStepConfigModal.stepIdx ? { ...st, settings: cfg } : st));
              setAiStepConfigModal(null);
              showToastMsg(aiStepConfigModal.stepType === 'ai_bizplan' ? 'AI 사업계획서 설정 저장됨' : 'AI 질문지 설정 저장됨');
            }}
            onClose={() => setAiStepConfigModal(null)}
            onNavigateToPreset={() => { setAiStepConfigModal(null); onNavigateToAI?.(); }}
          />
        );
      })()}
      {aiBizplanModal && (() => {
        const steps = aiBizplanModal.isEdit ? editSteps : formSteps;
        const curSettings = steps[aiBizplanModal.stepIdx]?.settings || {};
        return (
          <AIBizplanModal
            presets={store.aiPresets || []}
            initialPresetId={(curSettings.presetId as string) || ''}
            onSave={(cfg) => {
              const setter = aiBizplanModal.isEdit ? setEditSteps : setFormSteps;
              setter(p => p.map((st, i) => i === aiBizplanModal.stepIdx ? { ...st, settings: cfg } : st));
              setAiBizplanModal(null);
              showToastMsg('AI 사업계획서 설정 저장됨');
            }}
            onClose={() => setAiBizplanModal(null)}
          />
        );
      })()}
    </div>
  </>);
}

// ── 카테고리 ──
const DEPTH_BG: Record<number, string> = { 0: 'transparent', 1: '#F9FAFB', 2: '#F3F4F6' };

interface CatItem { id: string; name: string; depth: number; parent_id: string | null; sort_order: number }

function SortableCategoryItem({ cat, hoverId, setHoverId, editId, setEditId, editName, setEditName, handleEdit, handleDelete, setAddingParentId, setAddingName, hasChildrenFn }: {
  cat: CatItem;
  hoverId: string | null;
  setHoverId: (id: string | null) => void;
  editId: string | null;
  setEditId: (id: string | null) => void;
  editName: string;
  setEditName: (n: string) => void;
  handleEdit: (id: string) => void;
  handleDelete: (id: string, name: string) => void;
  setAddingParentId: (id: string | null) => void;
  setAddingName: (n: string) => void;
  hasChildrenFn: (id: string) => boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const isHover = hoverId === cat.id;
  const isEdit = editId === cat.id;
  const isLeaf = !hasChildrenFn(cat.id);
  const icon = isLeaf ? '\u{1F4C4}' : '\u{1F4C2}';
  const bg = DEPTH_BG[cat.depth] || '#F3F4F6';

  return (
    <div ref={setNodeRef} style={style}>
      <div
        onMouseEnter={() => setHoverId(cat.id)}
        onMouseLeave={() => setHoverId(null)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          paddingLeft: cat.depth * 24 + 12, paddingRight: 12, paddingTop: 7, paddingBottom: 7,
          background: bg, borderBottom: `1px solid ${C.border}`, fontSize: T.sm, position: 'relative',
        }}
      >
        {/* Drag handle */}
        <span {...attributes} {...listeners} style={{ cursor: 'grab', fontSize: T.md, color: C.muted, userSelect: 'none', lineHeight: 1 }} title="드래그하여 순서 변경">⠿</span>
        <span style={{ fontSize: T.md }}>{icon}</span>
        {isEdit ? (
          <input
            value={editName}
            onChange={e => setEditName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleEdit(cat.id); if (e.key === 'Escape') { setEditId(null); setEditName(''); } }}
            autoFocus
            style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: `1px solid ${C.accent}`, fontSize: T.sm, outline: 'none' }}
          />
        ) : (
          <span style={{ flex: 1, fontWeight: cat.depth === 0 ? 700 : 400, color: cat.depth === 0 ? C.text : C.sub }}>{cat.name}</span>
        )}
        {isHover && !isEdit && (
          <span style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button onClick={() => { setEditId(cat.id); setEditName(cat.name); }} title="수정"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: T.md, padding: '2px 4px', color: C.muted }}>&#9998;</button>
            <button onClick={() => handleDelete(cat.id, cat.name)} title="삭제"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: T.md, padding: '2px 4px', color: C.red }}>&#128465;</button>
            <button onClick={() => { setAddingParentId(cat.id); setAddingName(''); }} title="하위 추가"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: T.md, padding: '2px 4px', color: C.accent }}>+</button>
          </span>
        )}
      </div>
    </div>
  );
}

function CategoriesTab({ store }: { store: TMStore }) {
  const [localCategories, setLocalCategories] = useState<CatItem[]>(store.taskCategories);
  // Sync from store when store data changes (initial load)
  useEffect(() => { setLocalCategories(store.taskCategories); }, [store.taskCategories]);
  const taskCategories = localCategories;
  const [toast, setToast] = useState('');
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [addingParentId, setAddingParentId] = useState<string | null>(null);
  const [addingName, setAddingName] = useState('');
  const [addingRoot, setAddingRoot] = useState(false);
  const [rootName, setRootName] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  function showToastMsg(msg: string) { setToast(msg); setTimeout(() => setToast(''), 3000); }

  function getChildren(pid: string): CatItem[] {
    return taskCategories
      .filter(c => c.parent_id === pid)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }
  const hasChildren = (id: string) => taskCategories.some(c => c.parent_id === id);

  async function handleInlineAdd(parentId: string | null, parentDepth: number) {
    const name = parentId ? addingName : rootName;
    if (!name.trim()) return;
    const tempId = 'temp_' + Date.now();
    const newCat: CatItem = { id: tempId, name: name.trim(), parent_id: parentId || null, depth: parentDepth + 1, sort_order: taskCategories.length };
    // Optimistic update
    setLocalCategories(prev => [...prev, newCat]);
    setAddingParentId(null); setAddingName('');
    setAddingRoot(false); setRootName('');
    showToastMsg('카테고리 추가됨');
    try {
      const res = await api.createTaskCategory({ name: name.trim(), parent_id: parentId || null, depth: parentDepth + 1 });
      // Replace temp with real data from server
      const realCat = res.data || res;
      setLocalCategories(prev => prev.map(c => c.id === tempId ? { ...newCat, ...realCat } : c));
      await store.refresh();
    } catch (e: unknown) {
      // Rollback on failure
      setLocalCategories(prev => prev.filter(c => c.id !== tempId));
      showToastMsg(e instanceof Error ? e.message : '추가 실패');
    }
  }

  async function handleEdit(id: string) {
    if (!editName.trim()) return;
    const oldCat = taskCategories.find(c => c.id === id);
    // Optimistic update
    setLocalCategories(prev => prev.map(c => c.id === id ? { ...c, name: editName.trim() } : c));
    setEditId(null);
    const savedName = editName.trim();
    setEditName('');
    showToastMsg('카테고리 수정됨');
    try {
      await api.updateTaskCategory(id, { name: savedName });
      await store.refresh();
    } catch (e: unknown) {
      // Rollback on failure
      if (oldCat) setLocalCategories(prev => prev.map(c => c.id === id ? oldCat : c));
      showToastMsg(e instanceof Error ? e.message : '수정 실패');
    }
  }

  async function handleDelete(id: string, name: string) {
    // 하위 업무 템플릿 연결 시 삭제 불가
    try {
      const tRes = await api.getTaskTemplates();
      const tpls = tRes.data || [];
      const linkedCount = tpls.filter(t => (t.cat_path || []).includes(id)).length;
      if (linkedCount > 0) { showToastMsg(name + ' 카테고리에 ' + linkedCount + '개 업무가 연결되어 삭제할 수 없습니다.'); return; }
    } catch { /* ignore */ }
    if (!window.confirm('"' + name + '" 카테고리를 삭제하시겠습니까?')) return;
    const oldCategories = [...taskCategories];
    // Optimistic update - remove the category and its children
    const idsToRemove = new Set<string>();
    function collectIds(parentId: string) {
      idsToRemove.add(parentId);
      taskCategories.filter(c => c.parent_id === parentId).forEach(c => collectIds(c.id));
    }
    collectIds(id);
    setLocalCategories(prev => prev.filter(c => !idsToRemove.has(c.id)));
    showToastMsg('카테고리 삭제됨');
    try {
      await api.deleteTaskCategory(id);
      await store.refresh();
    } catch (e: unknown) {
      setLocalCategories(oldCategories);
      showToastMsg(e instanceof Error ? e.message : '삭제 실패');
    }
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);
    setDropIndicator(null);
    if (!over || active.id === over.id) return;

    const draggedCat = taskCategories.find(c => c.id === active.id);
    const overCat = taskCategories.find(c => c.id === over.id);
    if (!draggedCat || !overCat) return;

    // Same parent: reorder
    if (draggedCat.parent_id === overCat.parent_id) {
      const siblings = taskCategories
        .filter(c => c.parent_id === draggedCat.parent_id)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      const oldIndex = siblings.findIndex(c => c.id === active.id);
      const newIndex = siblings.findIndex(c => c.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(siblings, oldIndex, newIndex);
      const oldCategories = [...taskCategories];
      // Optimistic update: apply new sort_order locally
      const sortMap = new Map(reordered.map((c, i) => [c.id, i]));
      setLocalCategories(prev => prev.map(c => sortMap.has(c.id) ? { ...c, sort_order: sortMap.get(c.id)! } : c));
      showToastMsg('순서 변경됨');
      try {
        await Promise.all(reordered.map((c, i) => api.updateTaskCategory(c.id, { sort_order: i })));
      } catch (e: unknown) {
        setLocalCategories(oldCategories);
        showToastMsg(e instanceof Error ? e.message : '순서 변경 실패');
      }
    } else {
      // Different parent: move sub-category to new parent
      const newParentId = overCat.parent_id;
      const newDepth = overCat.depth;
      const oldCategories = [...taskCategories];
      // Optimistic update
      setLocalCategories(prev => prev.map(c => c.id === String(active.id) ? { ...c, parent_id: newParentId, depth: newDepth, sort_order: overCat.sort_order } : c));
      showToastMsg('카테고리 이동됨');
      try {
        await api.updateTaskCategory(String(active.id), { parent_id: newParentId, depth: newDepth, sort_order: overCat.sort_order });
      } catch (e: unknown) {
        setLocalCategories(oldCategories);
        showToastMsg(e instanceof Error ? e.message : '이동 실패');
      }
    }
  }

  const roots = taskCategories.filter(c => !c.parent_id).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const allIds = taskCategories.map(c => c.id);
  const activeCat = activeId ? taskCategories.find(c => c.id === activeId) : null;

  function renderTree(parentId: string | null): React.ReactNode {
    const items = parentId ? getChildren(parentId) : roots;
    return items.map(cat => (
      <div key={cat.id}>
        <SortableCategoryItem
          cat={cat}
          hoverId={hoverId} setHoverId={setHoverId}
          editId={editId} setEditId={setEditId}
          editName={editName} setEditName={setEditName}
          handleEdit={handleEdit} handleDelete={handleDelete}
          setAddingParentId={setAddingParentId} setAddingName={setAddingName}
          hasChildrenFn={hasChildren}
        />
        {dropIndicator === cat.id && (
          <div style={{ height: 2, background: C.accent, margin: '0 12px' }} />
        )}
        {addingParentId === cat.id && (
          <div style={{ paddingLeft: (cat.depth + 1) * 24 + 12, paddingRight: 12, paddingTop: 6, paddingBottom: 6, background: '#FFFBEB', borderBottom: `1px solid ${C.border}` }}>
            <input
              value={addingName}
              onChange={e => setAddingName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleInlineAdd(cat.id, cat.depth); if (e.key === 'Escape') { setAddingParentId(null); setAddingName(''); } }}
              autoFocus
              placeholder="하위 카테고리명 (Enter 저장, Esc 취소)"
              style={{ width: '100%', padding: '6px 10px', borderRadius: 4, border: `1px solid ${C.accent}`, fontSize: T.sm, outline: 'none', boxSizing: 'border-box' as const }}
            />
          </div>
        )}
        {renderTree(cat.id)}
      </div>
    ));
  }

  return (
    <div style={{ maxWidth: 600 }}>
      <TabTitle icon="📁" title="카테고리 관리" desc="업무 분류 체계를 트리 구조로 관리합니다." />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <SortableContext items={allIds} strategy={verticalListSortingStrategy}>
          <div style={{ borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, overflow: 'hidden', marginBottom: 12 }}>
            {roots.length === 0 && <Empty icon={'\u{1F4C2}'} message="카테고리가 없습니다" />}
            {renderTree(null)}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeCat ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', background: '#E8F0FE', borderRadius: R.sm,
              border: `2px solid ${C.accent}`, fontSize: T.sm, fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', opacity: 0.9,
            }}>
              <span style={{ fontSize: T.md, color: C.muted }}>⠿</span>
              <span>{activeCat.name}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      {addingRoot ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={rootName}
            onChange={e => setRootName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleInlineAdd(null, -1); if (e.key === 'Escape') { setAddingRoot(false); setRootName(''); } }}
            autoFocus
            placeholder="루트 카테고리명 (Enter 저장, Esc 취소)"
            style={{ flex: 1, padding: '8px 12px', borderRadius: R.sm, border: `1px solid ${C.accent}`, fontSize: T.sm, outline: 'none' }}
          />
          <button onClick={() => { setAddingRoot(false); setRootName(''); }}
            style={{ padding: '8px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, fontSize: T.sm, cursor: 'pointer' }}>취소</button>
        </div>
      ) : (
        <button onClick={() => setAddingRoot(true)}
          style={{ width: '100%', padding: 10, borderRadius: R.md, border: `2px dashed ${C.border}`, background: 'transparent', fontSize: T.sm, color: C.muted, cursor: 'pointer' }}>+ 루트 카테고리 추가</button>
      )}
      {toast && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', padding: '12px 24px', borderRadius: R.md, background: C.text, color: C.white, fontSize: T.sm, zIndex: 9999 }}>{toast}</div>}
    </div>
  );
}

// ── AI 프리셋 ──
const AI_DEFAULT_DOC_LABELS = ['사업계획서 양식', '평가기준표', '공고문', '크레탑 리포트', '현장 조사서', '기술명세서', '재무분석 자료', '대표자 이력서'];

interface PresetFile { label?: string; name: string; size: number; s3_key: string }

function AIPresetsTab({ store }: { store: TMStore }) {
  const { aiPresets } = store;
  const [selectedId, setSelectedId] = useState<string|null>(null);
  const [editForm, setEditForm] = useState<{ name: string; type: string; description: string; prompt: string; files: PresetFile[]; includeCollectFiles: boolean; docLabels: string[] } | null>(null);
  const [newDocLabel, setNewDocLabel] = useState('');
  const [showAddLabel, setShowAddLabel] = useState(false);
  const [toast, setToast] = useState('');

  function showToastMsg(msg: string) { setToast(msg); setTimeout(() => setToast(''), 3000); }

  function selectPreset(p: typeof aiPresets[0]) {
    const raw = p as unknown as Record<string, unknown>;
    setSelectedId(p.id);
    const dl = raw.doc_labels;
    setEditForm({
      name: p.name,
      type: p.type,
      description: (raw.description as string) || '',
      prompt: p.prompt || '',
      files: Array.isArray(raw.files) ? raw.files as PresetFile[] : [],
      includeCollectFiles: !!(raw.include_collect_files),
      docLabels: Array.isArray(dl) ? dl as string[] : [...AI_DEFAULT_DOC_LABELS],
    });
    setShowAddLabel(false); setNewDocLabel('');
  }

  function startAdd() {
    setSelectedId('new');
    setEditForm({ name: '', type: 'ai_questionnaire', description: '', prompt: '', files: [], includeCollectFiles: false, docLabels: [...AI_DEFAULT_DOC_LABELS] });
    setShowAddLabel(false); setNewDocLabel('');
  }

  const [uploadingLabel, setUploadingLabel] = useState('');

  async function handleUploadFile(file: File, label: string) {
    if (!selectedId || selectedId === 'new' || !editForm) { showToastMsg('먼저 프리셋을 저장한 후 파일을 업로드하세요'); return; }
    setUploadingLabel(label);
    try {
      const fd = new FormData();
      fd.append('files', file);
      fd.append('label', label);
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`/api/tm/ai-presets/${selectedId}/upload`, {
        method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd,
      });
      const json = await res.json();
      if (json.data?.allFiles) {
        setEditForm(p => p ? ({ ...p, files: json.data.allFiles }) : p);
      }
      showToastMsg(`${label} 업로드 완료`);
    } catch { showToastMsg('파일 업로드 실패'); }
    finally { setUploadingLabel(''); }
  }

  async function handleDeleteFile(s3Key: string) {
    if (!selectedId || selectedId === 'new') return;
    try {
      const token = localStorage.getItem('token') || '';
      await fetch(`/api/tm/ai-presets/${selectedId}/files?s3_key=${encodeURIComponent(s3Key)}`, {
        method: 'DELETE', headers: { Authorization: 'Bearer ' + token },
      });
      setEditForm(p => p ? ({ ...p, files: (p.files || []).filter(f => f.s3_key !== s3Key) }) : p);
      showToastMsg('파일 삭제됨');
    } catch { showToastMsg('파일 삭제 실패'); }
  }

  async function handleSave() {
    if (!editForm || !editForm.name.trim()) return;
    try {
      const payload = { name: editForm.name, type: editForm.type as 'ai_bizplan' | 'ai_questionnaire', prompt: editForm.prompt, include_collect_files: editForm.includeCollectFiles, doc_labels: editForm.docLabels } as Record<string, unknown>;
      if (selectedId === 'new') {
        await api.createAiPreset(payload as Partial<import('../types').AIPreset>);
      } else if (selectedId) {
        await api.updateAiPreset(selectedId, payload as Partial<import('../types').AIPreset>);
      }
      await store.refresh();
      if (selectedId === 'new') { setSelectedId(null); setEditForm(null); }
      showToastMsg(selectedId === 'new' ? 'AI 프리셋 등록됨' : '저장됨');
    } catch (e: unknown) { showToastMsg(e instanceof Error ? e.message : '실패'); }
  }

  async function handleDelete() {
    if (!selectedId || selectedId === 'new') return;
    if (!confirm('이 프리셋을 삭제하시겠습니까?')) return;
    try {
      await api.deleteAiPreset(selectedId);
      await store.refresh();
      setSelectedId(null); setEditForm(null);
      showToastMsg('프리셋 삭제됨');
    } catch (e: unknown) { showToastMsg(e instanceof Error ? e.message : '삭제 실패'); }
  }

  return (
    <div>
      <TabTitle icon="🤖" title="AI 프리셋" desc="AI 질문지·사업계획서 생성 시 사용할 파일과 프롬프트를 미리 설정합니다." />
      <div style={{ display: 'flex', height: 'calc(100vh - 200px)', overflow: 'hidden', border: `1px solid ${C.border}`, borderRadius: R.lg }}>
      {/* Left panel: preset list (280px) */}
      <div style={{ width: 280, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
          {aiPresets.length === 0 && <Empty icon="🤖" message="등록된 프리셋 없음" />}
          {aiPresets.map(p => {
            const act = selectedId === p.id;
            const typeBadge = p.type === 'ai_questionnaire' ? '질문지' : '계획서';
            const badgeColor = p.type === 'ai_questionnaire' ? C.purple : C.blue;
            const badgeBg = p.type === 'ai_questionnaire' ? C.purpleLt : C.accentLt;
            const fileLabels = Array.isArray((p as unknown as Record<string, unknown>).files) ? ((p as unknown as Record<string, unknown>).files as PresetFile[]).map(f => f.label || f.name) : [];
            return (
              <div key={p.id} onClick={() => selectPreset(p)} style={{
                padding: '12px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 6,
                border: `2px solid ${act ? C.purple : C.border}`,
                background: act ? C.purpleLt : C.white,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: T.sm, fontWeight: 700 }}>{p.name}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: T.xs, fontWeight: 600, background: badgeBg, color: badgeColor }}>{typeBadge}</span>
                </div>
                {fileLabels.length > 0 && (
                  <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 4 }}>
                    {fileLabels.map((d, i) => <span key={i} style={{ padding: '1px 6px', borderRadius: 8, fontSize: T.xs, fontWeight: 600, background: C.purpleLt, color: C.purple }}>{d}</span>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ padding: 10, borderTop: `1px solid ${C.border}` }}>
          <button onClick={startAdd} style={{ width: '100%', padding: 10, borderRadius: R.md, border: `2px dashed ${C.purple}`, background: C.purpleLt, color: C.purple, fontSize: T.sm, fontWeight: 700, cursor: 'pointer' }}>+ 새 AI 프리셋 추가</button>
        </div>
      </div>

      {/* Right panel: edit form (flex) */}
      <div style={{ flex: 1, overflow: 'auto', padding: editForm ? 20 : 0, background: 'transparent' }}>
        {!editForm ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: C.muted, fontSize: T.md }}>
            ← 프리셋을 선택하거나 새로 추가하세요
          </div>
        ) : (
          <div style={{ maxWidth: 500 }}>
            <div style={{ fontSize: T.md + 1, fontWeight: 800, marginBottom: 14 }}>
              {selectedId === 'new' ? '새 프리셋 추가' : '프리셋 편집'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, display: 'block', marginBottom: 4 }}>프리셋 이름 *</label>
              <input value={editForm.name} onChange={e => setEditForm(p => p ? ({ ...p, name: e.target.value }) : p)} placeholder="예: 벤처기업 인증 질문지"
                style={{ width: '100%', padding: '9px 12px', borderRadius: R.md, border: `1px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box' as const }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, display: 'block', marginBottom: 4 }}>유형</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {([['ai_questionnaire', '🤖 질문지'], ['ai_bizplan', '📊 계획서']] as const).map(([v, lb]) => (
                  <button key={v} onClick={() => setEditForm(p => p ? ({ ...p, type: v }) : p)}
                    style={{ flex: 1, padding: '10px 0', borderRadius: R.md, border: editForm.type === v ? `2px solid ${C.purple}` : `1px solid ${C.border}`, background: editForm.type === v ? C.purpleLt : '#F9FAFB', fontSize: T.sm, cursor: 'pointer', color: editForm.type === v ? C.purple : C.muted, fontWeight: editForm.type === v ? 700 : 400, transition: 'all 0.15s' }}>{lb}</button>
                ))}
              </div>
            </div>
            {/* AI에게 전달할 파일 */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: T.xs, fontWeight: 700, color: C.purple, display: 'block', marginBottom: 6 }}>📎 AI에게 전달할 파일</label>

              {/* 앞단계 자료수집 파일 자동 전달 — 토글 스위치 */}
              <div onClick={() => setEditForm(p => p ? ({ ...p, includeCollectFiles: !p.includeCollectFiles }) : p)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, height: 48, padding: '0 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#fff', marginBottom: 0, cursor: 'pointer', borderBottom: 'none', borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
                <span style={{ fontSize: T.md, flexShrink: 0 }}>🔄</span>
                <span style={{ flex: 1, fontSize: T.sm, color: C.text, fontWeight: 500 }}>앞단계 자료수집 파일 자동 전달</span>
                <div style={{ width: 38, height: 20, borderRadius: 10, background: editForm.includeCollectFiles ? '#059669' : '#D1D5DB', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: editForm.includeCollectFiles ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
              </div>
              <div style={{ height: 1, background: C.border, marginBottom: 3 }} />

              {/* 항목별 파일 업로드 행 */}
              {(editForm.docLabels || []).map(docLabel => {
                const uploaded = (editForm.files || []).find(f => f.label === docLabel);
                const isUploading = uploadingLabel === docLabel;
                return (
                  <div key={docLabel} style={{ display: 'flex', alignItems: 'center', gap: 8, height: 48, padding: '0 12px', borderRadius: 6, border: `1px solid ${uploaded ? C.purple : C.border}`, background: uploaded ? C.purpleLt : '#fff', marginBottom: 3 }}>
                    <span style={{ fontSize: T.md, flexShrink: 0 }}>{uploaded ? '✅' : '📌'}</span>
                    <span style={{ flex: 1, fontSize: T.sm, color: uploaded ? C.purple : C.text, fontWeight: uploaded ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {uploaded ? `${docLabel} — ${uploaded.name}` : docLabel}
                    </span>
                    {uploaded ? (
                      <button onClick={() => handleDeleteFile(uploaded.s3_key)}
                        style={{ fontSize: T.xs, padding: '4px 8px', borderRadius: R.sm, border: `1px solid ${C.red}33`, background: 'transparent', color: C.red, cursor: 'pointer', flexShrink: 0 }}>✕</button>
                    ) : selectedId && selectedId !== 'new' ? (
                      <label style={{ fontSize: T.xs, padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.purple}`, background: C.purpleLt, color: C.purple, cursor: isUploading ? 'default' : 'pointer', fontWeight: 600, flexShrink: 0, opacity: isUploading ? 0.6 : 1 }}>
                        {isUploading ? '⏳' : '📎 파일 선택'}
                        <input type="file" onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadFile(f, docLabel); e.target.value = ''; }} style={{ display: 'none' }} disabled={isUploading} />
                      </label>
                    ) : (
                      <span style={{ fontSize: T.xs, color: C.muted, flexShrink: 0 }}>저장 후 업로드</span>
                    )}
                    <button onClick={() => {
                      if (uploaded) handleDeleteFile(uploaded.s3_key);
                      setEditForm(p => p ? ({ ...p, docLabels: p.docLabels.filter(l => l !== docLabel) }) : p);
                    }}
                      style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: T.xs, padding: '2px 4px', flexShrink: 0 }} title="항목 제거">✕</button>
                  </div>
                );
              })}

              {/* 항목 추가 */}
              {showAddLabel ? (
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                  <input value={newDocLabel} onChange={e => setNewDocLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && newDocLabel.trim()) { setEditForm(p => p ? ({ ...p, docLabels: [...p.docLabels, newDocLabel.trim()] }) : p); setNewDocLabel(''); } }}
                    placeholder="항목명 입력" autoFocus
                    style={{ flex: 1, padding: '6px 10px', borderRadius: R.sm, border: `1px solid ${C.purple}`, fontSize: T.sm, outline: 'none' }} />
                  <button onClick={() => { if (newDocLabel.trim()) { setEditForm(p => p ? ({ ...p, docLabels: [...p.docLabels, newDocLabel.trim()] }) : p); setNewDocLabel(''); } }}
                    disabled={!newDocLabel.trim()}
                    style={{ padding: '6px 12px', borderRadius: R.sm, border: 'none', background: newDocLabel.trim() ? C.purple : '#ddd', color: '#fff', fontSize: T.xs, fontWeight: 700, cursor: newDocLabel.trim() ? 'pointer' : 'default' }}>추가</button>
                  <button onClick={() => { setShowAddLabel(false); setNewDocLabel(''); }}
                    style={{ padding: '6px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, fontSize: T.xs, cursor: 'pointer' }}>취소</button>
                </div>
              ) : (
                <button onClick={() => setShowAddLabel(true)}
                  style={{ width: '100%', padding: '7px 0', marginTop: 4, borderRadius: R.sm, border: `1.5px dashed ${C.purple}44`, background: 'transparent', color: C.purple, fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>+ 항목 추가</button>
              )}
            </div>

            {/* AI 지시사항 */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, display: 'block', marginBottom: 4 }}>📝 AI 지시사항 (프롬프트)</label>
              <textarea value={editForm.prompt || ''} onChange={e => setEditForm(p => p ? ({ ...p, prompt: e.target.value }) : p)}
                placeholder="AI에게 전달할 기본 지시사항" rows={4}
                style={{ width: '100%', padding: '10px 12px', borderRadius: R.md, border: `1px solid ${C.border}`, fontSize: T.sm, lineHeight: 1.7, outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const }} />
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8 }}>
              {selectedId !== 'new' && <button onClick={handleDelete} style={{ padding: '10px 16px', borderRadius: R.md, border: `1px solid ${C.red}`, background: C.redLt, color: C.red, fontSize: T.sm, cursor: 'pointer' }}>삭제</button>}
              <div style={{ flex: 1 }} />
              <button onClick={() => { setSelectedId(null); setEditForm(null); }} style={{ padding: '10px 16px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white, fontSize: T.sm, cursor: 'pointer' }}>취소</button>
              <button onClick={handleSave} disabled={!editForm.name.trim()} style={{ padding: '10px 24px', borderRadius: R.md, border: 'none', background: editForm.name.trim() ? C.purple : '#ddd', color: '#fff', fontSize: T.sm, fontWeight: 700, cursor: editForm.name.trim() ? 'pointer' : 'default' }}>💾 저장</button>
            </div>
          </div>
        )}
      </div>

      {toast && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', padding: '12px 24px', borderRadius: R.md, background: C.text, color: C.white, fontSize: T.sm, zIndex: 9999 }}>{toast}</div>}
    </div>
    </div>
  );
}

// ── 과금 현황 ──
function BillingTab() {
  // 로딩 중 layout shift 방지
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [stats, setStats] = useState<api.BillingStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [claudeUsage, setClaudeUsage] = useState<{ input_tokens: number; output_tokens: number; cost: number; requests: number } | null>(null);
  const [claudeLoading, setClaudeLoading] = useState(false);
  const [awsCost, setAwsCost] = useState(() => localStorage.getItem('billing_aws_cost') || '');

  // 최근 12개월 옵션 생성
  const monthOptions: { year: number; month: number; label: string }[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthOptions.push({ year: d.getFullYear(), month: d.getMonth() + 1, label: `${d.getFullYear()}년 ${d.getMonth() + 1}월` });
  }

  useEffect(() => {
    setLoading(true); setError('');
    api.getBillingStats(selYear, selMonth)
      .then(res => setStats(res.data))
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : '조회 실패');
        setStats({ sms: { short: 0, long: 0, shortCost: 0, longCost: 0 }, popbill: { count: 0, cost: 0 }, total: 0 });
      })
      .finally(() => setLoading(false));

    // Fetch Claude AI usage
    setClaudeLoading(true);
    const token = localStorage.getItem('token') || '';
    fetch(`/api/tm/claude-usage?year=${selYear}&month=${selMonth}`, {
      headers: { 'Authorization': 'Bearer ' + token },
    })
      .then(r => r.json())
      .then(data => setClaudeUsage(data.data || null))
      .catch(() => setClaudeUsage(null))
      .finally(() => setClaudeLoading(false));
  }, [selYear, selMonth]);

  const smsShortUnit = 15;
  const smsLongUnit = 27;
  const popbillUnit = 50;

  const smsShortCost = stats ? (stats.sms.shortCost || stats.sms.short * smsShortUnit) : 0;
  const smsLongCost = stats ? (stats.sms.longCost || stats.sms.long * smsLongUnit) : 0;
  const popbillCost = stats ? (stats.popbill.cost || stats.popbill.count * popbillUnit) : 0;
  const claudeCost = claudeUsage?.cost || 0;
  const awsCostNum = parseFloat(awsCost) || 0;
  const total = smsShortCost + smsLongCost + popbillCost + claudeCost + awsCostNum;

  return (
    <div style={{ maxWidth: 700 }}>
      <TabTitle icon="💳" title="과금 현황" desc="SMS·AI 사용량 및 요금 현황입니다." />
      <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:16}}>
        <select value={`${selYear}-${selMonth}`} onChange={e => { const [y, m] = e.target.value.split('-'); setSelYear(Number(y)); setSelMonth(Number(m)); }}
          style={{padding:'6px 12px', borderRadius:R.sm, border:'1px solid '+C.border, fontSize:T.sm, outline:'none', cursor:'pointer'}}>
          {monthOptions.map(o => (
            <option key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`}>{o.label}</option>
          ))}
        </select>
      </div>

      
      {error && <div style={{ padding: '10px 14px', borderRadius: R.md, background: '#FEF2F2', color: C.red, fontSize: T.xs, marginBottom: 12 }}>&#x26A0;&#xFE0F; {error} (기본값 표시)</div>}

      {/* 총 과금 요약 */}
      <div style={{ padding: 16, borderRadius: R.lg, background: `linear-gradient(135deg,${C.primary},#264D73)`, color: '#fff', marginBottom: 16, minHeight: 80 }}>
        <div style={{ fontSize: T.sm, opacity: 0.7 }}>{selYear}년 {selMonth}월 총 과금</div>
        <div style={{ fontSize: 28, fontWeight: 800, marginTop: 4 }}>{(total || 0).toLocaleString()}원</div>
      </div>

      {/* 항목별 과금 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20 }}>
        {[
          { label: '알리고 SMS (단문)', icon: '&#x1F4AC;', count: stats?.sms.short || 0, unit: smsShortUnit, cost: smsShortCost, color: '#059669' },
          { label: '알리고 SMS (장문)', icon: '&#x1F4DD;', count: stats?.sms.long || 0, unit: smsLongUnit, cost: smsLongCost, color: '#2563EB' },
          { label: '팝빌 기업정보조회', icon: '&#x1F50D;', count: stats?.popbill.count || 0, unit: popbillUnit, cost: popbillCost, color: '#D97706' },
        ].map(item => (
          <div key={item.label} style={{ padding: '14px 16px', borderRadius: R.md, border: `1px solid ${C.border}`, background: C.white }}>
            <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 8 }} dangerouslySetInnerHTML={{ __html: item.icon + ' ' + item.label }} />
            <div style={{ fontSize: 22, fontWeight: 800, color: item.color }}>{(item.count || 0).toLocaleString()}<span style={{ fontSize: T.xs, fontWeight: 400, color: C.muted }}>건</span></div>
            <div style={{ fontSize: T.xs, color: C.muted, marginTop: 4 }}>@{item.unit}원 = <span style={{ fontWeight: 700, color: item.color }}>{(item.cost || 0).toLocaleString()}원</span></div>
          </div>
        ))}
      </div>

      {/* 과금 상세 테이블 */}
      <div style={{ background: C.white, borderRadius: R.lg, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.sm }}>
          <thead>
            <tr style={{ background: C.bgAlt }}>
              {['항목', '건수', '단가', '금액'].map(h => (
                <th key={h} style={{ padding: '8px 14px', textAlign: h === '항목' ? 'left' : 'right', fontWeight: 600, color: C.sub, borderBottom: `1px solid ${C.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: '8px 14px', fontWeight: 600 }}>알리고 SMS (단문)</td>
              <td style={{ padding: '8px 14px', textAlign: 'right' }}>{(stats?.sms?.short || 0).toLocaleString()}</td>
              <td style={{ padding: '8px 14px', textAlign: 'right', color: C.muted }}>{smsShortUnit}원</td>
              <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700 }}>{(smsShortCost || 0).toLocaleString()}원</td>
            </tr>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: '8px 14px', fontWeight: 600 }}>알리고 SMS (장문)</td>
              <td style={{ padding: '8px 14px', textAlign: 'right' }}>{(stats?.sms?.long || 0).toLocaleString()}</td>
              <td style={{ padding: '8px 14px', textAlign: 'right', color: C.muted }}>{smsLongUnit}원</td>
              <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700 }}>{(smsLongCost || 0).toLocaleString()}원</td>
            </tr>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: '8px 14px', fontWeight: 600 }}>팝빌 기업정보조회</td>
              <td style={{ padding: '8px 14px', textAlign: 'right' }}>{(stats?.popbill?.count || 0).toLocaleString()}</td>
              <td style={{ padding: '8px 14px', textAlign: 'right', color: C.muted }}>{popbillUnit}원</td>
              <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700 }}>{(popbillCost || 0).toLocaleString()}원</td>
            </tr>
            <tr style={{ background: C.bgAlt }}>
              <td colSpan={3} style={{ padding: '10px 14px', fontWeight: 800, fontSize: T.md }}>합계</td>
              <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 800, fontSize: T.md, color: C.accent }}>{(total || 0).toLocaleString()}원</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Claude AI 사용량 */}
      <div style={{ background: C.white, borderRadius: R.lg, border: `1px solid ${C.border}`, overflow: 'hidden', marginTop: 16 }}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: C.bgAlt, fontSize: T.sm, fontWeight: 700 }}>
          🤖 Claude AI 사용량
        </div>
        <div style={{ padding: 16 }}>
          {claudeLoading ? (
            <div style={{ color: C.muted, fontSize: T.xs }}>로딩 중...</div>
          ) : claudeUsage ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
              <div style={{ padding: '10px 14px', borderRadius: R.md, border: `1px solid ${C.border}`, textAlign: 'center' }}>
                <div style={{ fontSize: T.xs, color: C.muted }}>요청 수</div>
                <div style={{ fontSize: T.xl, fontWeight: 800, color: C.text, marginTop: 4 }}>{(claudeUsage.requests || 0).toLocaleString()}</div>
              </div>
              <div style={{ padding: '10px 14px', borderRadius: R.md, border: `1px solid ${C.border}`, textAlign: 'center' }}>
                <div style={{ fontSize: T.xs, color: C.muted }}>Input 토큰</div>
                <div style={{ fontSize: T.xl, fontWeight: 800, color: '#2563EB', marginTop: 4 }}>{(claudeUsage.input_tokens || 0).toLocaleString()}</div>
              </div>
              <div style={{ padding: '10px 14px', borderRadius: R.md, border: `1px solid ${C.border}`, textAlign: 'center' }}>
                <div style={{ fontSize: T.xs, color: C.muted }}>Output 토큰</div>
                <div style={{ fontSize: T.xl, fontWeight: 800, color: '#7C3AED', marginTop: 4 }}>{(claudeUsage.output_tokens || 0).toLocaleString()}</div>
              </div>
              <div style={{ padding: '10px 14px', borderRadius: R.md, border: `1px solid ${C.border}`, textAlign: 'center' }}>
                <div style={{ fontSize: T.xs, color: C.muted }}>비용</div>
                <div style={{ fontSize: T.xl, fontWeight: 800, color: C.accent, marginTop: 4 }}>{(claudeUsage.cost || 0).toLocaleString()}원</div>
              </div>
            </div>
          ) : (
            <div style={{ color: C.muted, fontSize: T.xs }}>Claude AI 사용량 데이터가 없습니다</div>
          )}
        </div>
      </div>

      {/* AWS 비용 수동 입력 */}
      <div style={{ background: C.white, borderRadius: R.lg, border: `1px solid ${C.border}`, overflow: 'hidden', marginTop: 16 }}>
        <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.border}`, background: C.bgAlt, fontSize: T.sm, fontWeight: 700 }}>
          ☁️ AWS 인프라 비용 (수동 입력)
        </div>
        <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="number"
            value={awsCost}
            onChange={e => {
              setAwsCost(e.target.value);
              localStorage.setItem('billing_aws_cost', e.target.value);
            }}
            placeholder="월 AWS 비용 (원)"
            style={{ flex: 1, maxWidth: 250, padding: '8px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none' }}
          />
          <span style={{ fontSize: T.xs, color: C.muted }}>원 / 월</span>
        </div>
      </div>

      <div style={{ padding: '12px 16px', borderRadius: R.md, background: C.bgAlt, fontSize: T.xs, color: C.muted, lineHeight: 1.8, marginTop: 16 }}>
        &#x1F4A1; 단가 기준: 알리고 SMS 단문 15원, 장문 27원 / 팝빌 기업정보조회 50원/건
      </div>
    </div>
  );
}

// ── 자료수집 템플릿 ──
function CollectTemplatesTab() {
  const confirm = useConfirmModal();
  interface CollectTplGuide { type: 'link' | 'file'; label: string; url?: string; fileName?: string; filePath?: string; s3Key?: string }
  interface CollectTplItem { label: string; type: string; required?: boolean; sensitive?: boolean; desc?: string; guides?: CollectTplGuide[]; options?: string[] }
  interface CollectTpl { id: string; name: string; items?: CollectTplItem[]; item_count?: number; cnt?: number }
  const [templates, setTemplates] = useState<CollectTpl[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editItems, setEditItems] = useState<CollectTplItem[]>([]);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);
  const [newItemLabel, setNewItemLabel] = useState('');
  const [newItemType, setNewItemType] = useState('file');
  const [newItemReq, setNewItemReq] = useState(true);
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemSensitive, setNewItemSensitive] = useState(false);
  const [newItemGuides, setNewItemGuides] = useState<CollectTplGuide[]>([]);
  const [newItemOptions, setNewItemOptions] = useState<string[]>([]);
  const [editProjectGuides, setEditProjectGuides] = useState<CollectTplGuide[]>([]);
  const ITEM_TYPES: [string, string][] = [['file', '📎 파일'], ['text', '✏️ 단문'], ['text_long', '📝 장문'], ['radio', '○ 객관식'], ['checkbox', '☑ 체크박스']];
  const needsOptions = (t: string) => t === 'radio' || t === 'checkbox';

  function showToastMsg(msg: string) { setToast(msg); setTimeout(() => setToast(''), 3000); }

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch('/api/templates', { headers: { Authorization: 'Bearer ' + token } });
      const json = await res.json();
      const list = json.data || json;
      setTemplates(Array.isArray(list) ? list : []);
    } catch { setTemplates([]); }
  };

  useEffect(() => { fetchTemplates(); }, []);

  async function selectTemplate(id: string) {
    setSelId(id);
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`/api/templates/${id}`, { headers: { Authorization: 'Bearer ' + token } });
      const json = await res.json();
      const detail = json.data || json;
      setEditName(detail.name || '');
      setEditItems(Array.isArray(detail.items) ? detail.items.map((it: Record<string, unknown>) => ({
        label: (it.label as string) || '', type: (it.type as string) || 'file', required: it.required !== false,
        sensitive: !!(it.sensitive), desc: (it.desc as string) || (it.description as string) || '',
        guides: Array.isArray(it.guides) ? it.guides : [],
        options: Array.isArray(it.options) ? it.options : [],
      })) : []);
      setEditProjectGuides(Array.isArray(detail.projectGuides) ? detail.projectGuides.map((g: Record<string, unknown>) => ({
        type: (g.type as string) || 'link', label: (g.label as string) || '',
        url: (g.url as string) || '', fileName: (g.fileName as string) || (g.file_name as string) || '',
        s3Key: (g.s3Key as string) || (g.filePath as string) || (g.file_path as string) || '',
      })) : []);
    } catch { /* ignore */ }
  }

  async function handleCreate() {
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch('/api/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ name: '새 템플릿', items: [] }),
      });
      if (!res.ok) throw new Error('생성 실패');
      const json = await res.json();
      await fetchTemplates();
      const newId = (json.data || json)?.id;
      if (newId) selectTemplate(newId);
      showToastMsg('새 템플릿 생성됨');
    } catch (e: unknown) { showToastMsg(e instanceof Error ? e.message : '생성 실패'); }
  }

  function handleDelete(id: string) {
    confirm.ask('이 템플릿을 삭제하시겠습니까?', async () => {
      try {
        const token = localStorage.getItem('token') || '';
        const res = await fetch(`/api/templates/${id}`, { method: 'DELETE', headers: { Authorization: 'Bearer ' + token } });
        if (!res.ok) throw new Error('삭제 실패');
        await fetchTemplates();
        if (selId === id) { setSelId(null); setEditItems([]); setEditName(''); }
        showToastMsg('템플릿 삭제됨');
      } catch (e: unknown) { showToastMsg(e instanceof Error ? e.message : '삭제 실패'); }
    });
  }

  async function handleSave() {
    if (!selId || !editName.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || '';
      await fetch(`/api/templates/${selId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ name: editName.trim(), items: editItems.filter(it => it.label.trim()).map(it => ({
          label: it.label, type: it.type, required: it.required, sensitive: !!it.sensitive, desc: it.desc || '',
          guides: (it.guides || []).map(g => ({ type: g.type, label: g.label || '', url: g.url || '', fileName: g.fileName || '', filePath: g.filePath || g.s3Key || '', s3Key: g.s3Key || g.filePath || '' })),
          options: it.options || [],
        })), projectGuides: editProjectGuides.map(g => ({ type: g.type, label: g.label || '', url: g.url || '', fileName: g.fileName || '', filePath: g.filePath || g.s3Key || '', s3Key: g.s3Key || g.filePath || '' })) }),
      });
      await fetchTemplates();
      showToastMsg('저장 완료');
    } catch { showToastMsg('저장 실패'); }
    finally { setSaving(false); }
  }

  function addNewItem() {
    if (!newItemLabel.trim()) return;
    setEditItems(p => [...p, { label: newItemLabel.trim(), type: newItemType, required: newItemReq, sensitive: newItemSensitive, desc: newItemDesc, guides: [...newItemGuides], options: newItemOptions.filter(o => o.trim()) }]);
    setNewItemLabel(''); setNewItemType('file'); setNewItemReq(true); setNewItemSensitive(false); setNewItemDesc(''); setNewItemGuides([]); setNewItemOptions([]);
  }

  const sel = templates.find(t => t.id === selId);

  return (<>
    {confirm.el}
    <TabTitle icon="📎" title="자료수집 템플릿" desc="자료수집 시 사용할 항목 구성을 미리 설정합니다." />
    <div style={{ display: 'flex', height: 'calc(100vh - 160px)', gap: 0 }}>
      {/* Left panel: template list */}
      <div style={{ width: 240, flexShrink: 0, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', background: C.bgAlt }}>
        <div style={{ padding: '12px 10px', borderBottom: `1px solid ${C.border}` }}>
          <button onClick={handleCreate} style={{ width: '100%', padding: '8px 0', borderRadius: R.sm, border: `1.5px dashed ${C.accent}`, background: C.accentLt, color: C.accent, fontSize: T.xs, fontWeight: 700, cursor: 'pointer' }}>+ 새 템플릿</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {templates.map(t => {
            const itemCount = (t as unknown as Record<string, unknown>).itemCount as number || (t as unknown as Record<string, unknown>).item_count as number || (t as unknown as Record<string, unknown>).cnt as number || (t.items || []).length || 0;
            const isActive = selId === t.id;
            return (
              <div key={t.id} onClick={() => selectTemplate(t.id)}
                style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, background: isActive ? C.white : 'transparent' }}>
                <div style={{ fontSize: T.sm, fontWeight: isActive ? 700 : 400, color: isActive ? C.accent : C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                <div style={{ fontSize: T.xs, color: C.muted, marginTop: 2 }}>{itemCount}개 항목</div>
              </div>
            );
          })}
          {templates.length === 0 && <div style={{ padding: 16, textAlign: 'center', color: C.muted, fontSize: T.xs }}>템플릿 없음</div>}
        </div>
      </div>

      {/* Right panel: template detail editor */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {!selId && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: C.muted, fontSize: T.md }}>
            좌측에서 템플릿을 선택하세요
          </div>
        )}
        {selId && sel && (
          <div style={{ maxWidth: 700 }}>
            {/* Template name (inline edit) */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: T.xs, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 4 }}>템플릿명</label>
              <input value={editName} onChange={e => setEditName(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: R.md, border: `1.5px solid ${C.border}`, fontSize: T.lg, fontWeight: 700, outline: 'none', boxSizing: 'border-box' as const }} />
            </div>

            {/* Items list with drag reorder */}
            <div style={{ fontSize: T.sm, fontWeight: 700, color: C.text, marginBottom: 8 }}>수집 항목 ({editItems.length}개)</div>
            {editItems.length === 0 && <div style={{ padding: 16, textAlign: 'center', fontSize: T.xs, color: C.muted, background: C.bgAlt, borderRadius: R.md, marginBottom: 10 }}>항목이 없습니다. 아래에서 추가하세요.</div>}
            {editItems.map((it, i) => (
              <div key={i} draggable
                onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragIdx(i); }}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(i); }}
                onDragEnd={() => { setDragIdx(null); setDragOver(null); }}
                onDrop={e => { e.preventDefault(); if (dragIdx !== null && dragIdx !== i) { setEditItems(p => { const arr = [...p]; const [moved] = arr.splice(dragIdx, 1); arr.splice(i, 0, moved); return arr; }); } setDragIdx(null); setDragOver(null); }}
                style={{ padding: '10px 12px', borderRadius: R.sm, background: C.white, border: `1px solid ${C.border}`, marginBottom: 6, cursor: 'grab', opacity: dragIdx === i ? 0.4 : 1, borderTop: dragOver === i ? `2px solid ${C.accent}` : undefined, transition: 'opacity .15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ cursor: 'grab', color: C.muted, fontSize: T.md, flexShrink: 0 }}>⠿</span>
                  <input value={it.label} onChange={e => setEditItems(p => p.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                    style={{ flex: 1, padding: '6px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', fontWeight: 600 }} />
                  <select value={it.type} onChange={e => setEditItems(p => p.map((x, j) => j === i ? { ...x, type: e.target.value } : x))}
                    style={{ padding: '6px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none', background: C.white }}>
                    {ITEM_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <button onClick={() => setEditItems(p => p.map((x, j) => j === i ? { ...x, required: !x.required } : x))}
                    style={{ fontSize: T.xs, fontWeight: 600, padding: '3px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', background: it.required ? C.accent : C.bg, color: it.required ? '#fff' : C.muted, transition: 'all .15s' }}>
                    {it.required ? '필수 ✓' : '선택'}
                  </button>
                  <button onClick={() => setEditItems(p => p.map((x, j) => j === i ? { ...x, sensitive: !x.sensitive } : x))}
                    style={{ fontSize: T.xs, fontWeight: 600, padding: '3px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', background: it.sensitive ? '#D97706' : C.bg, color: it.sensitive ? '#fff' : C.muted, transition: 'all .15s' }}>
                    {it.sensitive ? '민감 ✓' : '일반'}
                  </button>
                  <button onClick={() => setEditItems(p => p.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: T.sm }}>✕</button>
                </div>
                <input value={it.desc || ''} onChange={e => setEditItems(p => p.map((x, j) => j === i ? { ...x, desc: e.target.value } : x))}
                  placeholder="설명글 (선택사항)"
                  style={{ width: '100%', marginTop: 6, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: R.sm, fontSize: T.xs, color: C.sub, outline: 'none', boxSizing: 'border-box' as const, background: C.bgAlt }} />
                {needsOptions(it.type) && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: T.xs, color: C.sub, marginBottom: 3, fontWeight: 600 }}>선택지 ({it.type === 'radio' ? '단일선택' : '복수선택'})</div>
                    {(it.options || []).map((opt, oi) => (
                      <div key={oi} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 3 }}>
                        <span style={{ fontSize: T.xs, color: C.muted, minWidth: 18, textAlign: 'center' }}>{it.type === 'radio' ? '○' : '□'}</span>
                        <input value={opt} onChange={e => { const v = e.target.value; setEditItems(p => p.map((x, j) => j === i ? { ...x, options: (x.options || []).map((o, oj) => oj === oi ? v : o) } : x)); }}
                          placeholder={`선택지 ${oi + 1}`}
                          style={{ flex: 1, padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: T.xs, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }} />
                        <button onClick={() => setEditItems(p => p.map((x, j) => j === i ? { ...x, options: (x.options || []).filter((_, oj) => oj !== oi) } : x))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: T.xs }}>✕</button>
                      </div>
                    ))}
                    <button onClick={() => setEditItems(p => p.map((x, j) => j === i ? { ...x, options: [...(x.options || []), ''] } : x))}
                      style={{ background: 'none', border: `1px dashed ${C.border}`, borderRadius: 4, padding: '2px 8px', fontSize: 9, color: C.sub, cursor: 'pointer' }}>+ 선택지 추가</button>
                  </div>
                )}
                {/* Guides */}
                <div style={{ marginTop: 6 }}>
                  {(it.guides || []).map((g, gi) => (
                    <div key={gi} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4, padding: '4px 6px', background: '#F9FAFB', borderRadius: 4, fontSize: T.xs }}>
                      <span style={{ color: C.muted, fontSize: 9, minWidth: 28 }}>{g.type === 'link' ? '링크' : '파일'}</span>
                      <input value={g.label || ''} onChange={e => { const v = e.target.value; setEditItems(p => p.map((x, j) => { if (j !== i) return x; const gs = [...(x.guides || [])]; gs[gi] = { ...gs[gi], label: v }; return { ...x, guides: gs }; })); }}
                        placeholder="버튼 문구" style={{ flex: 1, padding: '2px 6px', border: `1px solid ${C.border}`, borderRadius: 3, fontSize: T.xs, outline: 'none', background: '#fff', boxSizing: 'border-box' as const, minWidth: 60 }} />
                      {g.type === 'link' && <input value={g.url || ''} onChange={e => { const v = e.target.value; setEditItems(p => p.map((x, j) => { if (j !== i) return x; const gs = [...(x.guides || [])]; gs[gi] = { ...gs[gi], url: v }; return { ...x, guides: gs }; })); }}
                        placeholder="https://..." style={{ flex: 1, padding: '2px 6px', border: `1px solid ${C.border}`, borderRadius: 3, fontSize: T.xs, outline: 'none', background: '#fff', boxSizing: 'border-box' as const, minWidth: 80 }} />}
                      {g.type === 'file' && <span style={{ fontSize: 9, color: C.muted }}>{g.fileName || '(파일 미지정)'}</span>}
                      <button onClick={() => setEditItems(p => p.map((x, j) => { if (j !== i) return x; const gs = (x.guides || []).filter((_, gj) => gj !== gi); return { ...x, guides: gs }; }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: T.xs }}>✕</button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                    {(it.guides || []).filter(g => g.type === 'link').length < 2 && (
                      <button onClick={() => setEditItems(p => p.map((x, j) => j === i ? { ...x, guides: [...(x.guides || []), { type: 'link' as const, label: '', url: '' }] } : x))}
                        style={{ background: 'none', border: `1px dashed ${C.border}`, borderRadius: 4, padding: '2px 8px', fontSize: 9, color: C.sub, cursor: 'pointer' }}>+ 가이드 링크</button>
                    )}
                    {(it.guides || []).filter(g => g.type === 'file').length < 2 && (
                      <button onClick={() => setEditItems(p => p.map((x, j) => j === i ? { ...x, guides: [...(x.guides || []), { type: 'file' as const, label: '', fileName: '' }] } : x))}
                        style={{ background: 'none', border: `1px dashed ${C.border}`, borderRadius: 4, padding: '2px 8px', fontSize: 9, color: C.sub, cursor: 'pointer' }}>+ 가이드 파일</button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* New item form */}
            <div style={{ marginTop: 10, padding: 14, background: C.accentLt, borderRadius: R.md, border: `1px solid ${C.accent}33` }}>
              <div style={{ fontSize: T.xs, fontWeight: 700, color: C.accent, marginBottom: 8 }}>새 항목 추가</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                <input value={newItemLabel} onChange={e => setNewItemLabel(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addNewItem(); }}
                  placeholder="항목명"
                  style={{ flex: 1, padding: '9px 12px', borderRadius: R.sm, border: `1.5px solid ${C.border}`, fontSize: T.md, outline: 'none', boxSizing: 'border-box' as const, background: '#fff' }} />
                <select value={newItemType} onChange={e => setNewItemType(e.target.value)}
                  style={{ padding: '9px 8px', borderRadius: R.sm, border: `1.5px solid ${C.border}`, fontSize: T.xs, outline: 'none', background: '#fff' }}>
                  {ITEM_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <button onClick={() => setNewItemReq(!newItemReq)}
                  style={{ fontSize: T.xs, fontWeight: 600, padding: '3px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', background: newItemReq ? C.accent : C.bg, color: newItemReq ? '#fff' : C.muted }}>
                  {newItemReq ? '필수 ✓' : '선택'}
                </button>
                <button onClick={() => setNewItemSensitive(!newItemSensitive)}
                  style={{ fontSize: T.xs, fontWeight: 600, padding: '3px 10px', borderRadius: 10, border: 'none', cursor: 'pointer', background: newItemSensitive ? '#D97706' : C.bg, color: newItemSensitive ? '#fff' : C.muted }}>
                  {newItemSensitive ? '민감 ✓' : '일반'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                <input value={newItemDesc} onChange={e => setNewItemDesc(e.target.value)}
                  placeholder="설명글 (선택사항)"
                  style={{ flex: 1, padding: '9px 12px', borderRadius: R.sm, border: `1.5px solid ${C.border}`, fontSize: T.sm, outline: 'none', boxSizing: 'border-box' as const, background: '#fff' }} />
                {needsOptions(newItemType) && <button onClick={() => setNewItemOptions(p => [...p, ''])}
                  style={{ padding: '9px 10px', borderRadius: R.sm, border: `1px dashed ${C.border}`, background: '#fff', fontSize: T.xs, color: C.sub, cursor: 'pointer', whiteSpace: 'nowrap' as const }}>+ 선택지</button>}
                {newItemGuides.filter(g => g.type === 'link').length < 2 && <button onClick={() => setNewItemGuides(p => [...p, { type: 'link' as const, label: '', url: '' }])}
                  style={{ padding: '9px 10px', borderRadius: R.sm, border: `1px dashed ${C.border}`, background: '#fff', fontSize: T.xs, color: C.sub, cursor: 'pointer', whiteSpace: 'nowrap' as const }}>+ 가이드 링크</button>}
                {newItemGuides.filter(g => g.type === 'file').length < 2 && <button onClick={() => setNewItemGuides(p => [...p, { type: 'file' as const, label: '', fileName: '' }])}
                  style={{ padding: '9px 10px', borderRadius: R.sm, border: `1px dashed ${C.border}`, background: '#fff', fontSize: T.xs, color: C.sub, cursor: 'pointer', whiteSpace: 'nowrap' as const }}>+ 가이드 파일</button>}
                <button onClick={addNewItem}
                  style={{ padding: '9px 16px', borderRadius: R.sm, border: 'none', background: C.accent, color: '#fff', fontSize: T.xs, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' as const }}>항목 추가</button>
              </div>
              {needsOptions(newItemType) && newItemOptions.length > 0 && (
                <div style={{ marginBottom: 6, padding: '8px 10px', background: '#fff', borderRadius: R.sm, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: T.xs, color: C.sub, marginBottom: 4, fontWeight: 600 }}>선택지 ({newItemType === 'radio' ? '단일선택' : '복수선택'})</div>
                  {newItemOptions.map((opt, oi) => (
                    <div key={oi} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 3 }}>
                      <span style={{ fontSize: T.xs, color: C.muted, minWidth: 18, textAlign: 'center' }}>{newItemType === 'radio' ? '○' : '□'}</span>
                      <input value={opt} onChange={e => { const v = e.target.value; setNewItemOptions(p => p.map((x, j) => j === oi ? v : x)); }}
                        placeholder={`선택지 ${oi + 1}`}
                        style={{ flex: 1, padding: '4px 8px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: T.xs, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }} />
                      <button onClick={() => setNewItemOptions(p => p.filter((_, j) => j !== oi))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: T.xs }}>✕</button>
                    </div>
                  ))}
                  <button onClick={() => setNewItemOptions(p => [...p, ''])}
                    style={{ background: 'none', border: `1px dashed ${C.border}`, borderRadius: 4, padding: '2px 8px', fontSize: 9, color: C.sub, cursor: 'pointer' }}>+ 선택지 추가</button>
                </div>
              )}
              {newItemGuides.length > 0 && newItemGuides.map((g, gi) => (
                <div key={gi} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4, padding: '3px 6px', background: '#fff', borderRadius: 4, fontSize: T.xs }}>
                  <span style={{ color: C.muted, fontSize: 9, minWidth: 28 }}>{g.type === 'link' ? '링크' : '파일'}</span>
                  <input value={g.label || ''} onChange={e => { const v = e.target.value; setNewItemGuides(p => p.map((x, j) => j === gi ? { ...x, label: v } : x)); }}
                    placeholder="버튼 문구" style={{ flex: 1, padding: '2px 6px', border: `1px solid ${C.border}`, borderRadius: 3, fontSize: T.xs, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }} />
                  {g.type === 'link' && <input value={g.url || ''} onChange={e => { const v = e.target.value; setNewItemGuides(p => p.map((x, j) => j === gi ? { ...x, url: v } : x)); }}
                    placeholder="https://..." style={{ flex: 1, padding: '2px 6px', border: `1px solid ${C.border}`, borderRadius: 3, fontSize: T.xs, outline: 'none', background: '#fff', boxSizing: 'border-box' as const }} />}
                  {g.type === 'file' && <span style={{ fontSize: 9, color: C.muted }}>{g.fileName || '(파일 미지정)'}</span>}
                  <button onClick={() => setNewItemGuides(p => p.filter((_, j) => j !== gi))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: T.xs }}>✕</button>
                </div>
              ))}
            </div>

            {/* 참고자료 (포털 상단 표시) */}
            <div style={{ fontSize: T.sm, fontWeight: 700, color: C.text, marginBottom: 8, marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📎 참고자료 <span style={{ fontWeight: 400, fontSize: T.xs, color: C.muted }}>(포털 상단에 표시)</span></span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => setEditProjectGuides(p => [...p, { type: 'link' as const, label: '', url: '' }])}
                  style={{ padding: '4px 10px', borderRadius: R.sm, border: `1px dashed ${C.border}`, background: C.white, fontSize: T.xs, color: C.sub, cursor: 'pointer' }}>+ 링크</button>
                <button onClick={() => setEditProjectGuides(p => [...p, { type: 'file' as const, label: '', fileName: '' }])}
                  style={{ padding: '4px 10px', borderRadius: R.sm, border: `1px dashed ${C.border}`, background: C.white, fontSize: T.xs, color: C.sub, cursor: 'pointer' }}>+ 파일</button>
              </div>
            </div>
            {editProjectGuides.length === 0 && <div style={{ padding: 12, textAlign: 'center', fontSize: T.xs, color: C.muted, background: C.bgAlt, borderRadius: R.md, marginBottom: 10 }}>참고자료가 없습니다</div>}
            {editProjectGuides.map((g, gi) => (
              <div key={gi} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, padding: '8px 10px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: R.sm }}>
                <span style={{ fontSize: T.xs, color: '#92400E', fontWeight: 600, flexShrink: 0, minWidth: 28 }}>{g.type === 'link' ? '🔗' : '📎'}</span>
                <input value={g.label || ''} onChange={e => { const v = e.target.value; setEditProjectGuides(p => p.map((x, j) => j === gi ? { ...x, label: v } : x)); }}
                  placeholder="표시 문구" style={{ width: 90, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: R.sm, fontSize: T.xs, outline: 'none' }} />
                {g.type === 'link' ? (
                  <input value={g.url || ''} onChange={e => { const v = e.target.value; setEditProjectGuides(p => p.map((x, j) => j === gi ? { ...x, url: v } : x)); }}
                    placeholder="https://..." style={{ flex: 1, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: R.sm, fontSize: T.xs, outline: 'none' }} />
                ) : (
                  <span style={{ flex: 1, fontSize: T.xs, color: C.muted }}>{g.fileName || '(파일 미지정)'}</span>
                )}
                <button onClick={() => setEditProjectGuides(p => p.filter((_, j) => j !== gi))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: T.xs }}>✕</button>
              </div>
            ))}

            {/* Save/Delete buttons */}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={() => handleDelete(selId)}
                style={{ padding: '10px 20px', borderRadius: R.md, border: `1px solid ${C.red}44`, background: C.redLt, color: C.red, fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>
                🗑️ 삭제
              </button>
              <div style={{ flex: 1 }} />
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '10px 32px', borderRadius: R.md, border: 'none', background: `linear-gradient(135deg, ${C.accent}, ${C.primary})`, color: '#fff', fontSize: T.sm, fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? '저장 중...' : '💾 저장'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    {toast && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50)', padding: '12px 24px', borderRadius: R.md, background: C.text, color: C.white, fontSize: T.sm, zIndex: 9999 }}>{toast}</div>}
  </>);
}

// ── 소속 관리 ──
/** 고객 페이지 · FP 포털 「카카오톡 답하기 · 문자 답하기」가 가는 곳 (2026-10-12 지도사 「전화 말고 문자 · 카카오 채널톡」) */
function ClientContactTab() {
  const [kakao, setKakao] = useState('');
  const [sms, setSms] = useState('');
  const [morning, setMorning] = useState('');   // 업무 아침 문자 받을 번호 (2026-10-12)
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.getClientContact().then(r => { if (r.data) { setKakao(r.data.kakao_url || ''); setSms(r.data.sms_phone || ''); setMorning(r.data.morning_sms_phone || ''); } }).catch(() => setMsg('불러오지 못했어요'));
  }, []);
  async function save() {
    setSaving(true); setMsg('');
    try {
      const r = await api.putClientContact({ kakao_url: kakao.trim(), sms_phone: sms.trim(), morning_sms_phone: morning.trim() });
      setMsg(r.success ? '저장했어요 — 고객 페이지 · FP 포털에 바로 반영돼요' : (r.error || '저장하지 못했어요'));
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : '저장하지 못했어요'); }
    finally { setSaving(false); }
  }
  const inp = { width: '100%', maxWidth: 420, padding: '8px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', boxSizing: 'border-box' as const };
  return (
    <div style={{ padding: 20, maxWidth: 640 }}>
      <TabTitle icon="💬" title="고객 문의 연락처" desc="고객 페이지 · FP 포털의 「카카오톡 답하기 · 문자 답하기」 버튼이 가는 곳이에요. 전화 버튼은 없어요. 비워 두면 그 버튼이 안 보여요." />
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 4 }}>카카오톡 채널 채팅 주소</div>
        <input value={kakao} onChange={e => setKakao(e.target.value)} placeholder="https://pf.kakao.com/_xxxxx/chat" style={inp} />
        <div style={{ fontSize: T.xs, color: C.muted, marginTop: 4 }}>카카오톡 채널 관리자센터 › 채널 홈 주소 뒤에 /chat 을 붙여요. 누르면 업체 · 건 이름이 복사돼서 채팅창에 붙여 넣을 수 있어요.</div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 4 }}>문자 받을 번호</div>
        <input value={sms} onChange={e => setSms(e.target.value)} placeholder="010-0000-0000" style={inp} />
        <div style={{ fontSize: T.xs, color: C.muted, marginTop: 4 }}>휴대폰에서는 문자 앱이 내용이 채워진 채로 열리고, PC 에서는 이 번호와 「번호 복사」만 보여요.</div>
      </div>
      <div style={{ marginBottom: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
        <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub, marginBottom: 4 }}>아침 업무 문자 받을 번호 <span style={{ fontWeight: 400, color: C.muted }}>· 지도사님 본인</span></div>
        <input value={morning} onChange={e => setMorning(e.target.value)} placeholder="010-0000-0000 (비우면 안 보내요)" style={inp} />
        <div style={{ fontSize: T.xs, color: C.muted, marginTop: 4 }}>평일 아침 8시 30분, 「내 차례 경과 · 오늘 · 회신 대기」 건수와 급한 5건을 문자로 보내요. 공휴일 · 주말과 내 차례가 0건인 날은 안 보내요. 여러 번호는 쉼표로.</div>
      </div>
      <button onClick={save} disabled={saving} style={{ padding: '8px 18px', borderRadius: R.sm, border: 'none', background: saving ? C.muted : C.accent, color: C.white, fontSize: T.sm, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}>{saving ? '저장 중' : '저장'}</button>
      {msg && <span style={{ marginLeft: 10, fontSize: T.xs, color: C.sub }}>{msg}</span>}
    </div>
  );
}

function CompanyMgmtTab() {
  const [list, setList] = useState<api.InsuranceCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [addName, setAddName] = useState('');
  const [addColor, setAddColor] = useState('#0ea55a');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [toast, setToast] = useState('');
  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(''), 3000); }

  const DEFAULT_COMPANIES: { name: string; color: string }[] = [
    { name: '한화생명', color: '#FF6600' },
    { name: 'DB생명', color: '#003366' },
    { name: '교보생명', color: '#006633' },
    { name: '삼성생명', color: '#1428A0' },
    { name: 'KB생명', color: '#FFCD00' },
    { name: '신한생명', color: '#0046FF' },
    { name: '미래에셋생명', color: '#E50012' },
    { name: '메트라이프생명', color: '#00A9E0' },
  ];

  useEffect(() => {
    api.getInsuranceCompanies()
      .then(async (res) => {
        const data = res.data || [];
        if (data.length === 0) {
          // Auto-seed default companies
          try {
            for (let i = 0; i < DEFAULT_COMPANIES.length; i++) {
              await api.createInsuranceCompany({ name: DEFAULT_COMPANIES[i].name, color: DEFAULT_COMPANIES[i].color, sort_order: i + 1 });
            }
            const refreshed = await api.getInsuranceCompanies();
            setList(refreshed.data || []);
          } catch {
            setList(DEFAULT_COMPANIES.map((c, i) => ({ id: 'co_' + (i + 1), account_id: '', name: c.name, color: c.color, sort_order: i + 1 })));
          }
        } else {
          setList(data);
        }
      })
      .catch(() => {
        setList(DEFAULT_COMPANIES.map((c, i) => ({ id: 'co_' + (i + 1), account_id: '', name: c.name, color: c.color, sort_order: i + 1 })));
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleAdd() {
    if (!addName.trim()) return;
    try {
      const res = await api.createInsuranceCompany({ name: addName.trim(), color: addColor, sort_order: list.length + 1 });
      setList(p => [...p, res.data]);
      setAddName(''); setAddColor('#0ea55a');
      showToast('✓ 추가되었습니다');
    } catch {
      // DB 미구현 시 로컬 추가
      setList(p => [...p, { id: 'co_' + Date.now(), account_id: '', name: addName.trim(), color: addColor, sort_order: p.length + 1 }]);
      setAddName(''); setAddColor('#0ea55a');
    }
  }

  async function handleSave(co: api.InsuranceCompany) {
    if (!editName.trim()) return;
    try {
      const res = await api.updateInsuranceCompany(co.id, { name: editName.trim(), color: editColor });
      setList(p => p.map(c => c.id === co.id ? res.data : c));
    } catch {
      setList(p => p.map(c => c.id === co.id ? { ...c, name: editName.trim(), color: editColor } : c));
    }
    setEditId(null);
    showToast('✓ 저장되었습니다');
  }

  async function handleDelete(co: api.InsuranceCompany) {
    if (!confirm(co.name + ' 삭제?')) return;
    try {
      await api.deleteInsuranceCompany(co.id);
      setList(p => p.filter(c => c.id !== co.id));
    } catch {
      setList(p => p.filter(c => c.id !== co.id));
    }
    showToast('✓ 삭제되었습니다');
  }

  if (loading) return <div style={{ padding: 20, color: C.muted }}>⏳ 로딩 중...</div>;

  return (
    <div style={{ maxWidth: 600 }}>
      <TabTitle icon="🏢" title="소속 관리" desc="FP 등록 시 선택 가능한 소속 보험사 목록입니다." />
      {list.map(co => {
        const isEd = editId === co.id;
        return (
          <div key={co.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: R.md, border: `1px solid ${C.border}`, marginBottom: 6, background: isEd ? C.accentLt : C.white }}>
            {isEd ? (<>
              <input value={editName} onChange={e => setEditName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSave(co); }}
                style={{ flex: 1, padding: '6px 10px', borderRadius: R.sm, border: `1px solid ${C.accent}`, fontSize: T.sm, outline: 'none' }} />
              <input type="color" value={editColor} onChange={e => setEditColor(e.target.value)} style={{ width: 36, height: 30, border: 'none', cursor: 'pointer', borderRadius: R.sm }} />
              <button onClick={() => handleSave(co)} style={{ padding: '5px 10px', borderRadius: R.sm, border: 'none', background: C.accent, color: '#fff', fontSize: T.xs, fontWeight: 700, cursor: 'pointer' }}>저장</button>
              <button onClick={() => setEditId(null)} style={{ padding: '5px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, fontSize: T.xs, cursor: 'pointer' }}>취소</button>
            </>) : (<>
              <div style={{ width: 20, height: 20, borderRadius: R.sm, background: co.color, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: T.sm, fontWeight: 600 }}>{co.name}</span>
              <span style={{ fontSize: T.xs, color: C.muted, fontFamily: 'monospace' }}>{co.color}</span>
              <button onClick={() => { setEditId(co.id); setEditName(co.name); setEditColor(co.color); }}
                style={{ padding: '4px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, fontSize: T.xs, cursor: 'pointer' }}>수정</button>
              <button onClick={() => handleDelete(co)}
                style={{ padding: '4px 8px', borderRadius: R.sm, border: 'none', background: C.redLt, color: C.red, fontSize: T.xs, cursor: 'pointer' }}>삭제</button>
            </>)}
          </div>
        );
      })}
      {list.length === 0 && <Empty icon="🏢" message="등록된 보험사가 없습니다" />}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, padding: '12px 14px', borderRadius: R.md, border: `2px dashed ${C.accent}`, background: C.accentLt }}>
        <input value={addName} onChange={e => setAddName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
          placeholder="새 보험사명 (예: 삼성생명)"
          style={{ flex: 1, padding: '7px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none' }} />
        <input type="color" value={addColor} onChange={e => setAddColor(e.target.value)} style={{ width: 36, height: 32, border: 'none', cursor: 'pointer', borderRadius: R.sm }} />
        <button onClick={handleAdd} disabled={!addName.trim()}
          style={{ padding: '7px 16px', borderRadius: R.sm, border: 'none', background: addName.trim() ? C.accent : C.borderDk, color: '#fff', fontSize: T.sm, fontWeight: 700, cursor: addName.trim() ? 'pointer' : 'default' }}>추가</button>
      </div>
      {toast && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', padding: '12px 24px', borderRadius: R.md, background: C.text, color: '#fff', fontSize: T.sm, zIndex: 9999 }}>{toast}</div>}
    </div>
  );
}

// ── 월별 상담료 통계 ──
function FeeStatsTab({ store }: { store: TMStore }) {
  const { consultations } = store;
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const months = ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'];

  const monthlyStats = months.map((label, mi) => {
    const mStr = `${viewYear}-${String(mi + 1).padStart(2, '0')}`;
    const mc = consultations.filter(c => c.date?.startsWith(mStr));
    const paid = mc.filter(c => c.fee_type === 'paid');
    const free = mc.filter(c => c.fee_type === 'free');
    const totalAmount = paid.reduce((s, c) => s + (c.fee_amount || 0), 0);
    const individual = paid.filter(c => c.fee_method === 'individual');
    const faSettle = paid.filter(c => c.fee_method === 'fa_settle');
    return { label, total: mc.length, paid: paid.length, free: free.length, totalAmount, individual: individual.length, faSettle: faSettle.length };
  });

  const yearTotal = monthlyStats.reduce((s, m) => ({
    total: s.total + m.total, paid: s.paid + m.paid, free: s.free + m.free,
    amount: s.amount + m.totalAmount, individual: s.individual + m.individual, faSettle: s.faSettle + m.faSettle,
  }), { total: 0, paid: 0, free: 0, amount: 0, individual: 0, faSettle: 0 });

  const maxAmount = Math.max(...monthlyStats.map(m => m.totalAmount), 1);

  return (
    <div style={{ maxWidth: 700 }}>
      <TabTitle icon="💰" title="상담료 통계" desc="월별 동행상담료 집계 현황입니다." />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <button onClick={() => setViewYear(y => y - 1)} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer' }}>◀</button>
        <span style={{ fontSize: T.xl, fontWeight: 800 }}>{viewYear}년</span>
        <button onClick={() => setViewYear(y => y + 1)} style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer' }}>▶</button>
      </div>

      {/* 연간 요약 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
        {[
          { l: '총 상담', v: yearTotal.total + '건', c: C.text },
          { l: '지불 금액', v: yearTotal.amount.toLocaleString() + '원', c: C.accent },
          { l: '지불/면제', v: `${yearTotal.paid}/${yearTotal.free}건`, c: '#059669' },
          { l: '개별/FA', v: `${yearTotal.individual}/${yearTotal.faSettle}건`, c: '#7C3AED' },
        ].map((s, i) => (
          <div key={i} style={{ padding: '12px 14px', borderRadius: R.md, background: C.white, border: `1px solid ${C.border}`, textAlign: 'center' }}>
            <div style={{ fontSize: T.xs, color: C.muted }}>{s.l}</div>
            <div style={{ fontSize: T.xl, fontWeight: 800, color: s.c, marginTop: 4 }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* 월별 바 차트 */}
      <div style={{ background: C.white, borderRadius: R.lg, border: `1px solid ${C.border}`, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: T.sm, fontWeight: 700, marginBottom: 12 }}>📊 월별 상담료</div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 120 }}>
          {monthlyStats.map((m, i) => {
            const h = maxAmount > 0 ? Math.max(4, (m.totalAmount / maxAmount) * 100) : 4;
            return (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: T.xs, color: C.accent, fontWeight: 700, marginBottom: 2 }}>{m.totalAmount > 0 ? Math.round(m.totalAmount / 10000) + '만' : ''}</div>
                <div style={{ height: h, background: m.totalAmount > 0 ? `linear-gradient(180deg,${C.accent},${C.primary})` : C.border, borderRadius: '3px 3px 0 0', transition: 'height 0.3s' }} />
                <div style={{ fontSize: T.xs, color: C.muted, marginTop: 4 }}>{m.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 월별 테이블 */}
      <div style={{ background: C.white, borderRadius: R.lg, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.xs }}>
          <thead>
            <tr style={{ background: C.bgAlt }}>
              {['월','총건수','지불','면제','금액','개별입금','FA정산'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, color: C.sub, borderBottom: `1px solid ${C.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthlyStats.map((m, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '7px 10px', textAlign: 'center', fontWeight: 600 }}>{m.label}</td>
                <td style={{ textAlign: 'center' }}>{m.total}</td>
                <td style={{ textAlign: 'center', color: C.accent, fontWeight: m.paid > 0 ? 700 : 400 }}>{m.paid}</td>
                <td style={{ textAlign: 'center', color: '#059669' }}>{m.free}</td>
                <td style={{ textAlign: 'right', paddingRight: 10, fontWeight: 700 }}>{m.totalAmount > 0 ? m.totalAmount.toLocaleString() + '원' : '-'}</td>
                <td style={{ textAlign: 'center' }}>{m.individual || '-'}</td>
                <td style={{ textAlign: 'center' }}>{m.faSettle || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── DB 관리 탭 ──
function DbToolsTab({ store, onNavigate }: { store: TMStore; onNavigate?: (id: string, params?: unknown) => void }) {
  const { fps, wms, companies } = store;
  const [section, setSection] = useState<'fp' | 'wm' | 'company'>('fp');

  return (
    <div>
      <TabTitle icon="🛠️" title="DB 관리" desc="FP, WM, 업체 정보 관리 및 엑셀 업로드/다운로드" />

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {([['fp', '👤 FP 정보'], ['wm', '🏆 WM 정보'], ['company', '🏢 업체 정보']] as [typeof section, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setSection(id)}
            style={{ padding: '8px 16px', borderRadius: R.md, border: section === id ? 'none' : `1px solid ${C.border}`, background: section === id ? C.accent : C.white, color: section === id ? '#fff' : C.sub, fontSize: T.sm, fontWeight: section === id ? 700 : 400, cursor: 'pointer' }}>{label}</button>
        ))}
      </div>

      {section === 'fp' && <FPInfoSection fps={fps} onRefresh={store.refresh} />}
      {section === 'wm' && <WMInfoSection wms={wms} fps={fps} onRefresh={store.refresh} />}
      {section === 'company' && <CompanyInfoSection onNavigate={onNavigate} companies={companies} store={store} />}
    </div>
  );
}

// ── Excel 유틸 ──
function xlsxDownload(data: Record<string, string | number | null | undefined>[], filename: string) {
  import('xlsx').then(XLSX => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, filename);
  });
}

function xlsxParse(file: File): Promise<Record<string, string>[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      import('xlsx').then(XLSX => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: '' });
        resolve(rows);
      }).catch(reject);
    };
    reader.onerror = () => reject(new Error('파일 읽기 실패'));
    reader.readAsArrayBuffer(file);
  });
}

// ── FP 정보 섹션 ──
function FPInfoSection({ fps, onRefresh }: { fps: import('../types').FP[]; onRefresh: () => Promise<void> }) {
  const confirm = useConfirmModal();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState('');
  const [importing, setImporting] = useState(false);
  const PAGE_SIZE = 20;
  const [editingId, setEditingId] = useState<string|null>(null);
  const [editForm, setEditForm] = useState<Record<string, string>>({});

  const filtered = fps.filter(f => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return f.name.toLowerCase().includes(q) || (f.company || '').toLowerCase().includes(q) || (f.phone || '').includes(q);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleDownload() {
    xlsxDownload(fps.map(f => ({ '소속': f.company || '', '이름': f.name, '연락처': f.phone || '', '이메일': f.email || '', '담당WM': f.wm_name || '' })), 'FP_목록.xlsx');
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const rows = await xlsxParse(file);
      const mapped = rows.map(r => ({ name: r['이름'] || '', company: r['소속'] || '', phone: r['연락처'] || '', email: r['이메일'] || '' })).filter(r => r.name);
      if (mapped.length === 0) { setToast('&#x274C; 유효한 데이터가 없습니다'); setTimeout(() => setToast(''), 3000); return; }
      await api.importFps(mapped);
      await onRefresh();
      setToast(`&#x2705; ${mapped.length}건 임포트 완료`);
    } catch (err: unknown) { setToast('&#x274C; ' + (err instanceof Error ? err.message : '임포트 실패')); }
    finally { setImporting(false); e.target.value = ''; setTimeout(() => setToast(''), 3000); }
  }

  return (<>
    {confirm.el}
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="🔍 FP 검색 (이름/소속/연락처)" style={{ flex: 1, padding: '7px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none' }} />
        <button onClick={handleDownload} style={{ padding: '7px 14px', fontSize: T.xs, fontWeight: 700, borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer' }}>📥 엑셀 다운로드</button>
        <label style={{ padding: '7px 14px', borderRadius: R.sm, border: 'none', background: C.accent, color: '#fff', fontSize: T.xs, cursor: 'pointer', fontWeight: 700 }}>
          {importing ? '⏳...' : '📤 엑셀 업로드'}
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} style={{ display: 'none' }} />
        </label>
      </div>

      <div style={{ background: C.white, borderRadius: R.lg, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.xs }}>
          <thead>
            <tr style={{ background: C.bgAlt }}>
              {['#', '소속', '이름', '연락처', '이메일', '담당WM', ''].map(h => (
                <th key={h || '_act'} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: C.sub, borderBottom: `1px solid ${C.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((f, i) => {
              const isEditing = editingId === f.id;
              return (
                <tr key={f.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '6px 10px', color: C.muted }}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                  {isEditing ? (
                    <>
                      <td style={{ padding: '6px 10px' }}><input value={editForm.company||''} onChange={e=>setEditForm(p=>({...p,company:e.target.value}))} style={{padding:'2px 6px',fontSize:T.xs,border:`1px solid ${C.border}`,borderRadius:4,outline:'none',width:'100%',boxSizing:'border-box'}} /></td>
                      <td style={{ padding: '6px 10px' }}><input value={editForm.name||''} onChange={e=>setEditForm(p=>({...p,name:e.target.value}))} style={{padding:'2px 6px',fontSize:T.xs,border:`1px solid ${C.border}`,borderRadius:4,outline:'none',width:'100%',boxSizing:'border-box'}} /></td>
                      <td style={{ padding: '6px 10px' }}><input value={editForm.phone||''} onChange={e=>setEditForm(p=>({...p,phone:e.target.value}))} style={{padding:'2px 6px',fontSize:T.xs,border:`1px solid ${C.border}`,borderRadius:4,outline:'none',width:'100%',boxSizing:'border-box'}} /></td>
                      <td style={{ padding: '6px 10px' }}><input value={editForm.email||''} onChange={e=>setEditForm(p=>({...p,email:e.target.value}))} style={{padding:'2px 6px',fontSize:T.xs,border:`1px solid ${C.border}`,borderRadius:4,outline:'none',width:'100%',boxSizing:'border-box'}} /></td>
                      <td style={{ padding: '6px 10px', color: C.accent }}>{f.wm_name || '-'}</td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '6px 10px' }}>{f.company || '-'}</td>
                      <td style={{ padding: '6px 10px', fontWeight: 600 }}>{f.name}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{f.phone || '-'}</td>
                      <td style={{ padding: '6px 10px', color: C.muted }}>{f.email || '-'}</td>
                      <td style={{ padding: '6px 10px', color: C.accent }}>{f.wm_name || '-'}</td>
                    </>
                  )}
                  <td style={{ padding: '6px 10px', display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    {isEditing ? (
                      <button onClick={async () => {
                        try { await api.updateFp(f.id, editForm); await onRefresh(); setEditingId(null); setToast('&#x2705; 저장 완료'); setTimeout(() => setToast(''), 3000); } catch { setToast('&#x274C; 저장 실패'); setTimeout(() => setToast(''), 3000); }
                      }} style={{ fontSize: T.xs, padding: '3px 8px', borderRadius: R.sm, border: 'none', background: C.accent, color: '#fff', cursor: 'pointer', fontWeight: 700 }}>저장</button>
                    ) : (
                      <button onClick={() => { setEditingId(f.id); setEditForm({ name: f.name, company: f.company || '', phone: f.phone || '', email: f.email || '' }); }}
                        style={{ fontSize: T.xs, padding: '3px 8px', borderRadius: R.sm, border: `1px solid ${C.accent}`, background: C.accentLt, color: C.accent, cursor: 'pointer' }}>수정</button>
                    )}
                    <button onClick={() => {
                      confirm.ask(`${f.name} FP를 삭제하시겠습니까?\n목록에서 숨겨지며, 같은 번호로 다시 등록하면 복구됩니다.`, async () => {
                        try { await api.deleteFp(f.id); await onRefresh(); setToast('&#x2705; 삭제 완료'); setTimeout(() => setToast(''), 3000); } catch { setToast('&#x274C; 삭제 실패'); setTimeout(() => setToast(''), 3000); }
                      });
                    }} style={{ fontSize: T.xs, padding: '2px 8px', border: '1px solid #DC262633', color: '#DC2626', background: 'transparent', borderRadius: R.sm, cursor: 'pointer' }}>삭제</button>
                  </td>
                </tr>
              );
            })}
            {paged.length === 0 && <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: C.muted }}>데이터 없음</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 12 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: page === 1 ? 'default' : 'pointer', color: page === 1 ? C.muted : C.text, fontSize: T.xs }}>&#x25C0;</button>
          <span style={{ padding: '4px 10px', fontSize: T.xs, color: C.sub }}>{page} / {totalPages} ({filtered.length}건)</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: page === totalPages ? 'default' : 'pointer', color: page === totalPages ? C.muted : C.text, fontSize: T.xs }}>&#x25B6;</button>
        </div>
      )}
      {toast && <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: R.md, background: C.text, color: '#fff', fontSize: T.xs, textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: toast }} />}
    </div>
  </>);
}

// ── WM 정보 섹션 ──
function WMInfoSection({ wms, fps, onRefresh }: { wms: import('../types').WM[]; fps: import('../types').FP[]; onRefresh: () => Promise<void> }) {
  const confirm = useConfirmModal();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState('');
  const [importing, setImporting] = useState(false);
  const PAGE_SIZE = 20;
  const [editingId, setEditingId] = useState<string|null>(null);
  const [editForm, setEditForm] = useState<Record<string, string>>({});

  const filtered = wms.filter(w => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return w.name.toLowerCase().includes(q) || (w.company || '').toLowerCase().includes(q) || (w.phone || '').includes(q);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleDownload() {
    xlsxDownload(wms.map(w => ({ '소속': w.company || '', '이름': w.name, '연락처': w.phone || '', '이메일': w.email || '' })), 'WM_목록.xlsx');
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const rows = await xlsxParse(file);
      const mapped = rows.map(r => ({ name: r['이름'] || '', company: r['소속'] || '', phone: r['연락처'] || '', email: r['이메일'] || '' })).filter(r => r.name);
      if (mapped.length === 0) { setToast('&#x274C; 유효한 데이터가 없습니다'); setTimeout(() => setToast(''), 3000); return; }
      await api.importWms(mapped);
      await onRefresh();
      setToast(`&#x2705; ${mapped.length}건 임포트 완료`);
    } catch (err: unknown) { setToast('&#x274C; ' + (err instanceof Error ? err.message : '임포트 실패')); }
    finally { setImporting(false); e.target.value = ''; setTimeout(() => setToast(''), 3000); }
  }

  return (<>
    {confirm.el}
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="🔍 WM 검색 (이름/소속/연락처)" style={{ flex: 1, padding: '7px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none' }} />
        <button onClick={handleDownload} style={{ padding: '7px 14px', fontSize: T.xs, fontWeight: 700, borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer' }}>📥 엑셀 다운로드</button>
        <label style={{ padding: '7px 14px', borderRadius: R.sm, border: 'none', background: C.accent, color: '#fff', fontSize: T.xs, cursor: 'pointer', fontWeight: 700 }}>
          {importing ? '⏳...' : '📤 엑셀 업로드'}
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} style={{ display: 'none' }} />
        </label>
      </div>

      <div style={{ background: C.white, borderRadius: R.lg, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.xs }}>
          <thead>
            <tr style={{ background: C.bgAlt }}>
              {['#', '소속', '이름', '연락처', '이메일', ''].map(h => (
                <th key={h || '_act'} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: C.sub, borderBottom: `1px solid ${C.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((w, i) => {
              const isEditing = editingId === w.id;
              return (
                <tr key={w.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '6px 10px', color: C.muted }}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                  {isEditing ? (
                    <>
                      <td style={{ padding: '6px 10px' }}><input value={editForm.company||''} onChange={e=>setEditForm(p=>({...p,company:e.target.value}))} style={{padding:'2px 6px',fontSize:T.xs,border:`1px solid ${C.border}`,borderRadius:4,outline:'none',width:'100%',boxSizing:'border-box'}} /></td>
                      <td style={{ padding: '6px 10px' }}><input value={editForm.name||''} onChange={e=>setEditForm(p=>({...p,name:e.target.value}))} style={{padding:'2px 6px',fontSize:T.xs,border:`1px solid ${C.border}`,borderRadius:4,outline:'none',width:'100%',boxSizing:'border-box'}} /></td>
                      <td style={{ padding: '6px 10px' }}><input value={editForm.phone||''} onChange={e=>setEditForm(p=>({...p,phone:e.target.value}))} style={{padding:'2px 6px',fontSize:T.xs,border:`1px solid ${C.border}`,borderRadius:4,outline:'none',width:'100%',boxSizing:'border-box'}} /></td>
                      <td style={{ padding: '6px 10px' }}><input value={editForm.email||''} onChange={e=>setEditForm(p=>({...p,email:e.target.value}))} style={{padding:'2px 6px',fontSize:T.xs,border:`1px solid ${C.border}`,borderRadius:4,outline:'none',width:'100%',boxSizing:'border-box'}} /></td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '6px 10px' }}>{w.company || '-'}</td>
                      <td style={{ padding: '6px 10px', fontWeight: 600 }}>{w.name}</td>
                      <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{w.phone || '-'}</td>
                      <td style={{ padding: '6px 10px', color: C.muted }}>{w.email || '-'}</td>
                    </>
                  )}
                  <td style={{ padding: '6px 10px', display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    {isEditing ? (
                      <button onClick={async () => {
                        try { await api.updateWm(w.id, editForm); await onRefresh(); setEditingId(null); setToast('&#x2705; 저장 완료'); setTimeout(() => setToast(''), 3000); } catch { setToast('&#x274C; 저장 실패'); setTimeout(() => setToast(''), 3000); }
                      }} style={{ fontSize: T.xs, padding: '3px 8px', borderRadius: R.sm, border: 'none', background: C.accent, color: '#fff', cursor: 'pointer', fontWeight: 700 }}>저장</button>
                    ) : (
                      <button onClick={() => { setEditingId(w.id); setEditForm({ name: w.name, company: w.company || '', phone: w.phone || '', email: w.email || '' }); }}
                        style={{ fontSize: T.xs, padding: '3px 8px', borderRadius: R.sm, border: `1px solid ${C.accent}`, background: C.accentLt, color: C.accent, cursor: 'pointer' }}>수정</button>
                    )}
                    <button onClick={() => {
                      const wmFps = fps.filter(f => f.wm_id === w.id);
                      const msg = wmFps.length > 0
                        ? '이 WM에는 소속 FP ' + wmFps.map(f => f.name).join(', ') + '가 있습니다. 정말 삭제하시겠습니까?'
                        : '정말 삭제하시겠습니까?';
                      confirm.ask(msg, async () => {
                        try {
                          await api.deleteWm(w.id);
                          await onRefresh();
                          setToast('&#x2705; WM이 삭제되었습니다');
                          setTimeout(() => setToast(''), 3000);
                        } catch { setToast('&#x274C; 삭제 실패'); setTimeout(() => setToast(''), 3000); }
                      });
                    }} style={{ fontSize: T.xs, padding: '2px 8px', border: '1px solid #DC262633', color: '#DC2626', background: 'transparent', borderRadius: R.sm, cursor: 'pointer' }}>삭제</button>
                  </td>
                </tr>
              );
            })}
            {paged.length === 0 && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: C.muted }}>데이터 없음</td></tr>}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 12 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: page === 1 ? 'default' : 'pointer', color: page === 1 ? C.muted : C.text, fontSize: T.xs }}>&#x25C0;</button>
          <span style={{ padding: '4px 10px', fontSize: T.xs, color: C.sub }}>{page} / {totalPages} ({filtered.length}건)</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: page === totalPages ? 'default' : 'pointer', color: page === totalPages ? C.muted : C.text, fontSize: T.xs }}>&#x25B6;</button>
        </div>
      )}
      {toast && <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: R.md, background: C.text, color: '#fff', fontSize: T.xs, textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: toast }} />}
    </div>
  </>);
}

// ── 업체 정보 섹션 (+ 법인등록번호 일괄채우기) ──
function CompanyInfoSection({ companies, store, onNavigate }: { companies: import('../types').Company[]; store: TMStore; onNavigate?: (id: string, params?: unknown) => void }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState('');
  const [importing, setImporting] = useState(false);
  const PAGE_SIZE = 20;

  // Batch CRNO state
  const targets = companies.filter(c => c.biz_no && c.biz_no.replace(/[^0-9]/g, '').length === 10 && !c.company_reg_num);
  const [crnoRunning, setCrnoRunning] = useState(false);
  const [crnoResult, setCrnoResult] = useState<api.BatchFillCrnoResult | null>(null);
  const [crnoError, setCrnoError] = useState('');

  const filtered = companies.filter(c => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return c.name.toLowerCase().includes(q) || (c.biz_no || '').includes(q) || (c.ceo || '').toLowerCase().includes(q);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function handleDownload() {
    xlsxDownload(companies.map(c => ({
      '회사명': c.name, '사업자번호': c.biz_no || '', '법인등록번호': c.company_reg_num || '',
      '업종': c.biz_type || '', '대표자명': c.ceo || '', '대표자연락처': c.ceo_phone || '', '대표자이메일': c.ceo_email || '',
      '담당자명': c.manager || '', '담당자연락처': c.manager_phone || '', '담당자이메일': c.manager_email || ''
    })), '업체_목록.xlsx');
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const rows = await xlsxParse(file);
      const mapped = rows.map(r => ({
        name: r['회사명'] || '', biz_no: r['사업자번호'] || '', company_reg_num: r['법인등록번호'] || '',
        biz_type: r['업종'] || '', ceo: r['대표자명'] || '', ceo_phone: r['대표자연락처'] || '', ceo_email: r['대표자이메일'] || '',
        manager: r['담당자명'] || '', manager_phone: r['담당자연락처'] || '', manager_email: r['담당자이메일'] || ''
      })).filter(r => r.name);
      if (mapped.length === 0) { setToast('&#x274C; 유효한 데이터가 없습니다'); setTimeout(() => setToast(''), 3000); return; }
      await api.importCompanies(mapped);
      await store.refreshCompanies();
      setToast(`&#x2705; ${mapped.length}건 임포트 완료`);
    } catch (err: unknown) { setToast('&#x274C; ' + (err instanceof Error ? err.message : '임포트 실패')); }
    finally { setImporting(false); e.target.value = ''; setTimeout(() => setToast(''), 3000); }
  }

  async function handleCrnoRun() {
    if (!confirm(`${targets.length}개 업체의 법인등록번호를 팝빌 API로 일괄 조회합니다.\n계속하시겠습니까?`)) return;
    setCrnoRunning(true); setCrnoResult(null); setCrnoError('');
    try {
      const res = await api.batchFillCrno();
      setCrnoResult(res.data);
      await store.refreshCompanies();
    } catch (e: unknown) { setCrnoError(e instanceof Error ? e.message : '실행 실패'); }
    finally { setCrnoRunning(false); }
  }

  const crnoStatusColor = (s: string) => s === 'updated' ? C.green : s === 'skip' ? C.muted : C.red;
  const crnoStatusLabel = (s: string) => s === 'updated' ? '&#x2705; 업데이트' : s === 'skip' ? '— 스킵' : '&#x274C; 오류';

  return (
    <div>
      {/* Company table */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="🔍 업체 검색 (회사명/사업자번호/대표자)" style={{ flex: 1, padding: '7px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none' }} />
        <button onClick={handleDownload} style={{ padding: '7px 14px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, fontSize: T.xs, cursor: 'pointer', fontWeight: 600 }}>📥 엑셀 다운로드</button>
        <label style={{ padding: '7px 14px', borderRadius: R.sm, border: 'none', background: C.accent, color: '#fff', fontSize: T.xs, cursor: 'pointer', fontWeight: 700 }}>
          {importing ? '⏳...' : '📤 엑셀 업로드'}
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} style={{ display: 'none' }} />
        </label>
      </div>

      <div style={{ background: C.white, borderRadius: R.lg, border: `1px solid ${C.border}`, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.xs, minWidth: 900 }}>
          <thead>
            <tr style={{ background: C.bgAlt }}>
              {['#', '회사명', '사업자번호', '법인등록번호', '업종', '대표자명', '대표자연락처', '대표자이메일', '담당자명', '담당자연락처', '담당자이메일', '담당FP', ''].map(h => (
                <th key={h} style={{ padding: '8px 8px', textAlign: 'left', fontWeight: 600, color: C.sub, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((c, i) => (
              <tr key={c.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '6px 8px', color: C.muted }}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                <td style={{ padding: '6px 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>{c.name}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{c.biz_no || '-'}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: c.company_reg_num ? C.green : C.muted }}>{c.company_reg_num || '-'}</td>
                <td style={{ padding: '6px 8px', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.biz_type || ''}>{c.biz_type || '-'}</td>
                <td style={{ padding: '6px 8px' }}>{c.ceo || '-'}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{c.ceo_phone || '-'}</td>
                <td style={{ padding: '6px 8px', color: C.muted }}>{c.ceo_email || '-'}</td>
                <td style={{ padding: '6px 8px' }}>{c.manager || '-'}</td>
                <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{c.manager_phone || '-'}</td>
                <td style={{ padding: '6px 8px', color: C.muted }}>{c.manager_email || '-'}</td>
                <td style={{ padding: '6px 8px', fontSize: T.xs, color: C.accent }}>{c.fps && c.fps.length > 0 ? c.fps.map(f => f.name).join(', ') : '-'}</td>
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                  <button onClick={() => { if(onNavigate) onNavigate('companies', { companyId: c.id }); }} style={{ fontSize: T.xs, padding: '3px 8px', borderRadius: R.sm, border: '1px solid ' + C.border, background: C.white, color: C.sub, cursor: 'pointer', marginRight: 4 }}>수정</button>
                </td>
              </tr>
            ))}
            {paged.length === 0 && <tr><td colSpan={13} style={{ padding: 20, textAlign: 'center', color: C.muted }}>데이터 없음</td></tr>}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 12 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: page === 1 ? 'default' : 'pointer', color: page === 1 ? C.muted : C.text, fontSize: T.xs }}>&#x25C0;</button>
          <span style={{ padding: '4px 10px', fontSize: T.xs, color: C.sub }}>{page} / {totalPages} ({filtered.length}건)</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '4px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, cursor: page === totalPages ? 'default' : 'pointer', color: page === totalPages ? C.muted : C.text, fontSize: T.xs }}>&#x25B6;</button>
        </div>
      )}

      {toast && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', padding: '12px 24px', borderRadius: R.md, background: C.text, color: '#fff', fontSize: T.sm, zIndex: 9999 }} dangerouslySetInnerHTML={{ __html: toast }} />}
    </div>
  );
}
